import countries from 'i18n-iso-countries'
import countriesJSON from 'i18n-iso-countries/langs/de.json'

import { zodDeJSON as zodLanguageJSON } from '@/utils/zod/zod-i18n'

import { compensationDe } from './compensation/de'
import { workHoursDe } from './workHours/de'
import { network } from './network/de'
import { selfServiceDe } from './selfService/de'
import statesJSON from './states/de.json'

countries.registerLocale(countriesJSON)

export const de = {
    zod: {
        errors: {
            too_small: zodLanguageJSON.errors.too_small,
            too_big: zodLanguageJSON.errors.too_big,
        },
        custom: {
            eventServiceSelection: {
                required: 'Wähle mindestens einen optionalen Service.',
                single: 'Wähle höchstens einen optionalen Service.',
            },
            required: 'Muss ausgefüllt werden',
            invalidCountry: 'Ungültiges Land',
            invalidValue: 'Ungültiger Wert',
            invalidZip: 'Ungültige Postleitzahl',
            invalidState: 'Ungültiger Bundesstaat',
            invalidTime: 'Ungültige Zeit',
            invalidDateOnly: 'Ungültiges Datum (UTC-Stunden müssen null sein)',
            invalidTimeRange: 'Ungültiger Zeitbereich',
            invalidDateRange: 'Ungültiger Datumsbereich',
            invalidDateTimeRange: 'Ungültiger Datums- und Zeitbereich',
            invalidMinDate: 'Datum liegt vor dem Mindestdatum',
            invalidMaxDate: 'Datum liegt nach dem Höchstdatum',
            invalidRRule: 'Ungültige Zeichenfolge',
            invalidTaxID: 'Ungültige Steuernummer',
            invalidVATID: 'Ungültige USt-IdNr.',
            invalidCreditorIdentifier: 'Ungültige Gläubiger-Identifikationsnummer',
            invalidBIC: 'Ungültige BIC',
            invalidIBAN: 'Ungültige IBAN',
            invalidSepaMandateId: 'Ungültige SEPA-Mandats-ID',
            invalidObjectId: 'Ungültige Objekt-ID',
            invalidPhone: 'Ungültige Telefonnumer',
            invalidEmail: 'Ungültige E-Mail-Adresse',
            invalidOption: 'Ungültige Option',
            invalidMessageAttachments:
                'Verwende pro Nachricht nur eine Anhangsart. Sende Sprachnachrichten ohne Bildunterschrift.',
            invalidFileType: 'Ungültiger Dateityp',
            invalidFileSize: 'Die Datei überschreitet die maximal erlaubte Größe',
            chatPollDuplicateOption: 'Die IDs und Texte der Antwortmöglichkeiten müssen eindeutig sein.',
            chatPollDeadlineRequired: 'Lege ein Ende fest, wenn Ergebnisse bis dahin verborgen bleiben.',
            chatContentRequired:
                'Schreib einen Text oder füge mindestens einen Anhang hinzu. Ein Titel allein reicht nicht aus.',
            rawHtml: 'Nutze die Formatierung im Editor statt HTML.',
            unsafeLink: 'Nutze einen vollständigen Link mit http, https, mailto oder tel.',
            imageReference: 'Füge ein Bild aus den Anhängen dieses Beitrags ein.',
            chatDuplicateAttachment: 'Jeder Anhang muss eine eigene Kennung haben.',
            chatAttachmentsTooLarge: 'Die Anhänge überschreiten zusammen 2 GB.',
            invalidCalculatedValue: 'Berechneter Wert konnte nicht ausgewertet werden',
            invalidReference: 'Ungültige oder nicht existierende Referenz',
            invalidArray: 'Ungültiges Array',
            resourceRequired: 'Eine Datei muss ausgewählt werden',
            fromBeforeTo: 'Der Beginn muss vor dem Ende liegen',
            minBeforeMax: 'Der Mindestwert darf nicht größer als der Höchstwert sein',
            divisionByZero: 'Eine Division durch null ist nicht erlaubt',
            rruleMustEnd: 'Die angegebene Wiederholungsregel muss ein Enddatum haben',
            duplicate: 'Doppelter Wert',
            cannotChangeSingleMultiple:
                'Die Einstellung für wiederkehrende Einträge kann nach dem Speichern der Gruppe nicht mehr geändert werden',
            invalidRegex: 'Ungültiger regulärer Ausdruck',
            overpaymentDonationsWithoutAccount: 'Überzahlungen als Spende brauchen mindestens ein Spendenkonto',
            donationProposalOutcomeNotAllowed:
                'Ein Spendenvorschlagsergebnis ist nur für Bargeld-Transaktionsregeln zulässig, die Einnahmen entsprechen',
            invalidNumber: 'Ungültige Zahl',
            invalidCron: 'Ungültiger Cron-Ausdruck',
            contactRuleNotificationMessageOrDetailsRequired: 'Nachricht oder Details müssen ausgefüllt werden',
            onlinePaymentProviderRequired: 'Wähle mindestens einen Anbieter für Online-Zahlungen aus',
            iterateModeBaseIntervalMustBeLessThanIntervals:
                'Iterativer Modus kann nicht mit einem Basisintervall verwendet werden, das größer ist als alle flexiblen Intervalle',
        },
    },
    translation: {
        feedback: {
            releaseResolution:
                'Wir haben festgestellt, dass dieses Feedback in unserem jüngsten Release behoben oder umgesetzt wurde.',
        },
        bookingEmail: {
            noRefund: 'Eine Erstattung ist nicht möglich.',
            refundPricePercentage: 'Du erhältst {{value}} % des gezahlten Preises zurück.',
            refundPriceFee: 'Du erhältst den gezahlten Preis abzüglich {{value}} Stornierungsgebühr zurück.',
            refundPointsPercentage: 'Du erhältst {{value}} % der verwendeten Punkte zurück.',
            refundPointsFee: 'Du erhältst die verwendeten Punkte abzüglich {{value}} Punkten zurück.',
            cancelationUnavailable: 'Du kannst diese Buchung nicht online stornieren.',
            replaceStandard: 'Wähle zuerst eine andere Standardvorlage für diesen Buchungstyp aus.',
            templateLabel: 'E-Mail-Vorlage',
            defaultTemplate: 'Organisationsstandard verwenden',
            selectionHint:
                'Wähle eine veröffentlichte Veranstaltungsvorlage oder verwende den Organisationsstandard. Ist dieser nicht verfügbar, wird das mitgelieferte Layout verwendet.',
            selectionHintResource:
                'Wähle eine veröffentlichte Ressourcenvorlage. Bei Buchungen mehrerer Ressourcen mit unterschiedlichen Vorlagen wird der Organisationsstandard verwendet.',
            invalidSelection: 'Wähle eine veröffentlichte, aktive Vorlage für diesen Buchungstyp.',
            inUse: 'Diese Vorlage wird von einer Veranstaltung, Ressource oder Buchungsnachricht verwendet und kann nicht gelöscht, archiviert oder zurückgezogen werden.',
            ticket: 'Ticket',
            approvalRequired: 'Deine Buchung muss freigegeben werden. Dein Platz ist noch nicht bestätigt.',
            standard: 'Standard für diesen Buchungstyp',
            restore: 'Mitgeliefertes Layout laden',
            required: 'Erforderliche Buchungsfelder',
            attachmentHint:
                'Kalenderdateien, Belege und Buchungsanhänge haben Vorrang. Vorlagenanhänge nutzen die verbleibenden Plätze (insgesamt höchstens drei Dateien).',
            status: {
                confirmed: 'Bestätigt',
                pending: 'Bestätigung ausstehend',
                waitlisted: 'Auf der Warteliste',
                offered: 'Platz angeboten',
                checkedIn: 'Eingecheckt',
                canceled: 'Storniert',
            },
            fields: {
                waitlistNumber: 'Wartelistennummer',
                deadline: 'Erstattungsfrist',
                value: 'Antwort',
                label: 'Frage',
                group: 'Formularabschnitt',
                issuedPunchCardNumber: 'Verwendete Punktekarte',
                paymentMethod: 'Zahlungsart',
                organizationName: 'Organisation',
                title: 'Betreff',
                greeting: 'Begrüßung',
                content: 'Nachricht',
                bookingNumber: 'Buchungsnummer',
                totalPrice: 'Gesamtpreis',
                totalPoints: 'Punkte gesamt',
                discountCode: 'Rabattcode',
                period: 'Datum und Uhrzeit',
                timezone: 'Zeitzone',
                recurrence: 'Wiederholung',
                meetingTime: 'Treffpunktzeit',
                location: 'Ort',
                onlineInstructions: 'Teilnahmehinweise',
                name: 'Name',
                status: 'Status',
                recordNumber: 'Buchungsposition',
                rate: 'Tarif',
                variant: 'Variante',
                price: 'Preis',
                points: 'Punkte',
                quantity: 'Anzahl',
                offerValidTo: 'Annehmen bis',
                instructions: 'Hinweise',
                resource: 'Ressource',
                site: 'Standort',
                address: 'Adresse',
                text: 'Individueller Text',
                option: 'Option',
                number: 'Nummer',
                amount: 'Offener Betrag',
                dueDate: 'Zahlungsfrist',
                validTo: 'Gültig bis',
                reason: 'Grund',
                priceRefund: 'Erstattung',
                pointsRefund: 'Erstattete Punkte',
                caption: 'Buttontext',
                url: 'Aktionslink',
            },
            groups: {
                totals: 'Buchungssumme',
                policyTexts: 'Stornierungshinweise',
                refundPolicies: 'Erstattungsregeln',
                answers: 'Buchungsangaben',
                events: 'Veranstaltung',
                attendees: 'Teilnehmende und Tickets',
                reservations: 'Buchungszeiten',
                confirmationTexts: 'Bestätigungstext',
                waitlistTexts: 'Wartelistentext',
                resourceTexts: 'Ressourcenhinweise',
                services: 'Zusatzleistungen',
                payments: 'Zahlung',
                punchCards: 'Neue Punktekarten',
                cancelations: 'Stornierungen',
                actions: 'Aktionen',
            },
            validation: {
                languages: 'Buchungsvorlagen müssen deutsche und englische Inhalte enthalten.',
                missing:
                    'Erforderliche Felder fehlen oder sind ausgeblendet (prüfe Sprache, Wiederholungsbereich und Textmodus): {{fields}}',
                repeat: 'Ungültige Buchungswiederholung: {{repeat}}. Wiederhole die vollständige Buchungsliste.',
                binding: 'Ungültiger Buchungsplatzhalter: {{binding}}. Verwende die Felder des Buchungsschemas.',
            },
        },
        composition: {
            pass: {
                startsAt: 'Beginn der Veranstaltung',
                endsAt: 'Ende der Veranstaltung',
                back: 'Rückseite',
                noBackFields: 'Keine Felder auf der Rückseite',
                showMore: 'Weitere Zeilen anzeigen',
                showLess: 'Erste zwei Zeilen anzeigen',
            },
            expression: 'Ausdruck',
            pageNumber: 'Seitenzahl',
            pageCount: 'Seitenanzahl',
            invalidPdf: 'Diese Datei ist kein gültiges PDF-Dokument.',
            encryptedPdf: 'Passwortgeschützte PDF-Dateien kannst du nicht in einem Design verwenden.',
            interactivePdf: 'Das PDF enthält interaktive Elemente. Entferne sie, bevor du die Datei hochlädst.',
            duplicateAsset: 'Jedes Medium benötigt eine eindeutige Kennung.',
            invalidTemporaryAsset: 'Dieser Upload ist ungültig. Lade die Datei erneut hoch.',
            revisionConflict:
                'Dieses Design wurde während deiner Bearbeitung geändert. Lade es neu, bevor du erneut speicherst.',
            invalidTemplateChannels: 'Diese Designs passen nicht zum Vorlagentyp.',
            invalidResourceOwner: 'Dieses Medium gehört nicht zu dieser Vorlage.',
            pendingEmailDelivery:
                'Eine E-Mail dieser Unterhaltung wartet noch auf die Zustellung oder eine Klärung beim Versanddienst. Warte mit dem Löschen, bis der Vorgang abgeschlossen ist.',
            validation: {
                withProperty: '{{message}} Variable: {{property}}.',
                staticPassFieldRequired: 'Dieses Passfeld muss einen festen Wert für die Vorlage enthalten.',
                unknownProperty: 'Dieses Feld ist nicht verfügbar.',
                invalidScope: 'Diese Zeile ist an dieser Stelle im Design nicht verfügbar.',
                invalidExpression: 'Dieser Ausdruck ist ungültig.',
                invalidResultType: 'Der Werttyp passt nicht zu diesem Feld.',
                invalidContentPlacement:
                    'Diese Bindung kann an dieser Stelle oder in diesem Inhaltsformat nicht verwendet werden.',
                invalidValue: 'Dieser Designwert ist ungültig.',
            },
            propertiesInUse:
                'Diese Felder werden in gespeicherten Designs verwendet. Passe die Designs an, bevor du die Felder löschst oder ihre Typen änderst.',
            document: 'Dokument',
            platformEmail: {
                branding: 'Erscheinungsbild',
                productLogoUrl: 'Produktlogo',
                footerCaption: 'Fußzeilentext',
                organizationName: 'Organisationsname',
                organizationAvatar: 'Organisationslogo',
            },
        },
        templates: {
            defaultDesigns: {
                certificate: {
                    type: 'Zertifikatstyp:',
                    contact: 'Kontakt:',
                    validTo: 'Gültig bis:',
                    person: 'Name der Person:',
                },
                receipt: {
                    invoiceNumber: 'Rechnungsnummer',
                    offerNumber: 'Angebotsnummer',
                    confirmationNumber: 'Auftragsbest.-Nr.',
                    donationNumber: 'Spendennummer',
                    date: 'Datum',
                    dueDate: 'Fällig bis',
                    invoice: 'Rechnung',
                    position: 'Pos.',
                    description: 'Bezeichnung',
                    quantity: 'Menge',
                    unit: 'Einheit',
                    unitAmount: 'Einzel',
                    tax: 'Steuer',
                    discount: 'Rabatt %',
                    total: 'Gesamt',
                    donation: 'Spende',
                    amount: 'Betrag',
                    returnFee: 'Bankgebühren',
                    processingFee: 'Bearbeitungsgebühren',
                    reminder: {
                        receiptDate: 'Rechnungsdatum',
                        receiptDueDate: 'Fällig bis',
                        receiptGrossAmount: 'Rechnungsbetrag',
                        receiptFees: 'Gebühren',
                        unpaid: 'Ausstehende offene Summe – Stand',
                    },
                    returnedPayment: {
                        receiptDate: 'Vom',
                        receiptDueDate: 'Fällig zum',
                        receiptGrossAmount: 'Betrag',
                        unpaid: 'Offener Betrag zum',
                    },
                },
                booking: {
                    events: 'Veranstaltung',
                    attendees: 'Teilnehmende & Tickets',
                    goodToKnow: 'Gut zu wissen',
                    waitlistTexts: 'Informationen zur Warteliste',
                    reservations: 'Buchungszeiten',
                    services: 'Zusatzleistungen',
                    payments: 'Zahlung',
                    punchCards: 'Deine Punktekarte',
                    cancelations: 'Stornierung & Erstattung',
                },
                contact: {
                    title: 'Titel der Vorlage',
                    greeting: 'Hallo',
                    content: 'Dies ist ein Beispiel-Inhalt.',
                },
                donationNumber: 'Spendennummer',
            },
            starting: { contact: 'Kontaktvorlage', event: 'Veranstaltungsvorlage', resource: 'Ressourcenvorlage' },
            sourceTypeMismatch: 'Wähle eine bestehende Vorlage mit demselben Typ und Untertyp.',
            pendingOutput: 'Diese Vorlage wird für Ausgaben benötigt, die noch nicht erstellt wurden.',
            standardProtected:
                'Standardvorlagen können nicht gelöscht, archiviert oder zurückgezogen werden. Lege zuerst eine andere Standardvorlage fest.',
            lastSource: 'Behalte mindestens eine nicht archivierte Vorlage je Typ und Untertyp als Ausgangsvorlage.',
            pdfBackground: {
                noTemplates: 'Für die ausgewählten Typen wurden keine Briefvorlagen gefunden.',
                updateFailed:
                    'Die Aktualisierung ist nach {{count}} bestätigten Änderungen fehlgeschlagen. Einige Designs können bereits geändert sein. Versuche es erneut, um den Hintergrund auf alle ausgewählten Typen anzuwenden.',
            },
            transactional: {
                defaultName: { content: 'Transaktions-E-Mail', action: 'Transaktions-E-Mail mit Aktion' },
                replaceStandard: 'Wähle zuerst eine andere Standardvorlage für denselben Transaktionstyp.',
                actionRequired:
                    'Aktionsvorlagen müssen die Platzhalter für den Aktionslink und die Beschriftung in jeder Sprache enthalten.',
                contentRequired:
                    'Transaktionsvorlagen müssen den Inhaltsplatzhalter in jeder Sprache in einem Markdown-Textblock enthalten.',
                dispatchInUse:
                    'Diese Vorlage wird von bestehenden E-Mails verwendet. Archiviere sie, damit du diese weiterhin senden und erneut senden kannst.',
            },
            donationConfirmation: {
                defaultName: 'Spendenbestätigung',
                defaultName_germanReceipt: 'Anschreiben',
                invalidTemplate: 'Wähle eine veröffentlichte, nicht archivierte Vorlage für Spendenbestätigungen.',
                receiverRequired: 'Das PDF-Design muss den Platzhalter für die Empfängeradresse enthalten.',
            },
            receipt: {
                missingStandard: 'Für diesen Belegtyp ist keine veröffentlichte Standardvorlage eingerichtet.',

                invalidStandardContext: 'Diese Standardzuordnung passt nicht zum Vorlagentyp.',
                publishStandard: 'Veröffentliche die Vorlage, bevor du sie als Standard festlegst.',

                refundRequired: 'Betreff und Nachricht für Erstattungen sind erforderlich.',
                refundUnsupported:
                    'Diese Vorlage unterstützt keine Erstattungen. Du kannst dafür keine Erstattungsdaten festlegen.',
                receiverRequired: 'Das PDF-Design muss den Platzhalter für die Empfängeradresse enthalten.',
                replaceStandard: 'Wähle zuerst eine andere Standardvorlage für diese Belegart.',
                invalidEmailTemplate:
                    'Wähle eine veröffentlichte, nicht archivierte Standard-Kommunikationsvorlage ohne Aktion.',
                emailTemplateInUse: 'Diese Vorlage wird von einer Belegvorlage verwendet.',
            },
            placeholders: {
                content: 'Nachrichteninhalt',
                actionUrl: 'Aktionslink',
                actionCaption: 'Aktionsbeschriftung',
            },
        },
        socialMediaValidation: {
            count: 'Verwende höchstens {{count, number}} Fotos oder Videos.',
            mediaTypes: 'Nur Fotos und Videos werden unterstützt.',
            singleVideo: 'Ein Video muss die einzige Datei sein.',
            singleGif: 'Ein GIF muss die einzige Datei sein.',
            required: 'Füge ein Foto oder Video hinzu.',
            youtube: 'Füge genau ein Video hinzu.',
            google: 'Verwende höchstens ein Foto. Videos werden nicht unterstützt.',
            pinterest: 'Verwende ein Foto oder ein Video mit Titelbild.',
            file: 'Datei {{count, number}}',
            format: '{{file}}: Verwende {{formats}}.',
            empty: '{{file}} ist leer.',
            size: '{{file}} ist größer als {{count, number}} MB.',
        },
        socialTextValidation: {
            textLimit: 'Der Text ist länger als {{count, number}} Zeichen.',
            byteLimit: 'Der Text ist zu lang.',
            titleLimit: 'Der Titel ist länger als {{count, number}} Zeichen.',
            youtubeCharacters: 'YouTube-Titel und -Beschreibungen dürfen kein < oder > enthalten.',
        },
        websiteViewCss: {
            atRule: 'Nicht unterstützte CSS-Regel: @{{name}}. Verwende @media, @supports oder @container.',
            declaration: 'Deklarationen müssen zu einem CSS-Selektor gehören.',
            external: 'Externe Ressourcen und ausführbare Ausdrücke werden nicht unterstützt.',
            nesting: 'Verwende vollständige Selektoren statt verschachtelter Regeln oder &.',
            selector: 'Selektoren müssen mit einem Element, einer Klasse oder einem Attribut beginnen.',
        },
        actions: {
            saveChanges: 'Änderungen speichern',
        },
        contactAccessCriteria: {
            matchingHint:
                'Innerhalb jeder Kategorie (Kontakttypen, Segmente, Abteilungen oder Tags) muss ein ausgewählter Wert zutreffen. Alle Kategorien mit einer Auswahl müssen zutreffen. Leere Kategorien schränken nicht ein.',
            contactTypes: 'Kontakttypen',
            segments: 'Segmente',
            departments: 'Abteilungen',
            tags: 'Tags',
            all: 'Alle',
            invalidDepartment: 'Eine ausgewählte Abteilung ist nicht mehr verfügbar.',
            invalidSegment: 'Ein ausgewähltes Segment ist nicht mehr verfügbar.',
        },
        selfService: selfServiceDe,
        compensation: compensationDe,
        workHours: workHoursDe,
        network,
        reports: {
            tabs: { types: 'Typen' },
            list: {
                title: 'Liste',
                table: {
                    date: 'Datum',
                    user: 'Benutzer',
                    organization: 'Organisation',
                    category: 'Kategorie',
                    type: 'Typ',
                    description: 'Beschreibung',
                    note: 'Notiz',
                    createdAt: 'Erstellt am',
                },
                editor: {
                    spentMinutes: 'Aufgewendete Minuten',
                    billableMinutes: 'Abrechenbare Minuten',
                },
            },
            types: {
                name: 'Name',
                price: 'Nettopreis pro Stunde',
                descriptionRequired: 'Beschreibung erforderlich',
            },
        },
        ...statesJSON,
        language: {
            en: 'Englisch',
            de: 'Deutsch',
        },
        timeUnit: {
            millisecond: 'Millisekunde',
            milliseconds: 'Millisekunden',
            second: 'Sekunde',
            seconds: 'Sekunden',
            minute: 'Minute',
            minutes: 'Minuten',
            hour: 'Stunde',
            hours: 'Stunden',
            day: 'Tag',
            days: 'Tage',
            week: 'Woche',
            weeks: 'Wochen',
            month: 'Monat',
            months: 'Monate',
            quarter: 'Quartal',
            quarters: 'Quartale',
            year: 'Jahr',
            years: 'Jahre',
            halfYear: 'Halbjahr',
            halfYears: 'Halbjahre',
            halfYearAbbreviated: 'H-Jahr',
        },
        rruleIds: {
            repeats: 'Wiederholt sich',
            eventCount: 'Wiederholt sich {{count, number}}-mal',
            eventUntil: 'Wiederholt sich bis {{date}}',
            every: 'jede(n)',
            until: 'bis',
            for: 'für',
            in: 'in',
            the: 'der',
            and: 'und',
            or: 'oder',
            on: 'am',
            'on the': 'an dem',
            last: 'letzte(r)',
            st: 'ten',
            nd: 'ten',
            rd: 'ten',
            th: 'ten',
            time: 'mal',
            times: 'male',
            hour: 'Stunde',
            hours: 'Stunden',
            minute: 'Minute',
            minutes: 'Minuten',
            weekday: 'Wochentag',
            weekdays: 'Wochentage',
            day: 'Tag',
            days: 'Tage',
            week: 'Woche',
            weeks: 'Wochen',
            month: 'Monat',
            months: 'Monate',
            year: 'Jahr',
            years: 'Jahre',
        },
        libCommon: {
            finance: {
                accountingStatementEditorIssue: {
                    emptyStatementName: 'Der Berichtsname ist erforderlich.',
                    emptyStatementTitle: 'Der Berichtstitel ist erforderlich.',
                    emptyTemplate: 'Füge der Gewinnermittlung mindestens eine Zeile hinzu.',
                    emptyLineLabel: 'Jede Zeile benötigt eine Bezeichnung.',
                    duplicateSiblingLabel: 'Die Bezeichnung "{{label}}" ist im gleichen Abschnitt doppelt.',
                    emptyExpressionReference: 'Jede Formelzeile benötigt eine Referenz.',
                    missingExpressionReference: 'Die Zeile "{{reference}}" kann von hier nicht referenziert werden.',
                    missingAccount: 'Das Konto {{account}} existiert nicht im Kontenplan.',
                    invalidAccountRange: 'Der Bereich {{from}} - {{to}} ist ungültig.',
                    emptyAccountRange: 'Der Bereich {{from}} - {{to}} enthält kein bestehendes Konto.',
                },
            },
            work: {
                todo: {
                    conversationWithoutSubject: 'Konversation ohne Betreff',
                    errors: {
                        todoNotFound: 'Die Aufgabe wurde nicht gefunden',
                        assignmentNotFound: 'Die Zuweisung wurde nicht gefunden',
                        transactionRequired:
                            'Dieser Vorgang muss innerhalb einer Datenbanktransaktion ausgeführt werden',
                        parentMustBeOpen:
                            'Öffne zuerst die übergeordnete Aufgabe, bevor du eine Unteraufgabe hinzufügst oder erneut öffnest',
                        maximumChildren: 'Diese Aufgabe hat bereits die maximale Anzahl an Unteraufgaben',
                        invalidParent:
                            'Eine Aufgabe kann sich nicht selbst übergeordnet sein oder mehr als eine Ebene tief verschachtelt werden',
                        parentTopicMismatch:
                            'Eine Aufgabe und ihre übergeordnete Aufgabe müssen zum selben Thema gehören',
                        parentProjectMismatch:
                            'Eine Aufgabe und ihre übergeordnete Aufgabe müssen zum selben Projekt gehören',
                        parentContextMismatch:
                            'Eine Aufgabe und ihre übergeordnete Aufgabe müssen mit demselben Team, derselben Veranstaltung, derselben Ressource oder derselben Konversation verknüpft sein',
                        assigneesIncomplete: 'Alle zugewiesenen Personen müssen zuerst ihre Arbeit abschließen',
                        completionStateIncomplete: 'Der vorherige Aufgabenstatus fehlt',
                        customFieldsUnavailable: 'Dieses Thema definiert keine benutzerdefinierten Felder',
                        alreadyCompleted: 'Die Aufgabe ist bereits abgeschlossen',
                        itemChanged: 'Diese Aufgabe wurde geändert. Lade sie neu, bevor du es erneut versuchst.',
                        editLocked:
                            'Öffne diese Aufgabe erneut oder ziehe ihre Prüfung zurück, bevor du sie bearbeitest.',
                        reviewerUnavailable:
                            'Die prüfende Person benötigt Verwaltungszugriff und Leserechte für Aufgaben. Wähle eine berechtigte Person.',
                        alreadyWaitingForReview: 'Die Aufgabe wartet bereits auf Prüfung',
                        attachmentsChanged: 'Die Anhänge wurden geändert. Lade die Aufgabe vor dem Speichern neu.',
                        moveToTerminalStatus: 'Verwende für Prüf- und Abschlussstatus die Abschluss- oder Prüfaktion',
                        reviewerRequired: 'Für diesen Ablauf ist eine prüfende Person erforderlich',
                        subtodosNotAssigned: 'Diese Aufgabe hat Unteraufgaben, die dir nicht zugewiesen sind. Du kannst sie daher nicht löschen.',
                        subtodosNotAssignedForChange: 'Diese Aufgabe hat Unteraufgaben, die dir nicht zugewiesen sind. Du kannst daher ihren Kontext oder ihr Projekt nicht ändern.',
                        invalidAssignees: 'Nur Personen dieses Teams oder Events können zugewiesen werden',
                        noActiveReview: 'Keine aktive Prüfung gefunden',
                        reviewDecisionForbidden:
                            'Du musst die zugewiesene prüfende Person sein oder Aufgaben bearbeiten dürfen, um über diese Prüfung zu entscheiden',
                        completeSubtodosFirst: 'Schließe zuerst alle Unteraufgaben ab',
                        completedStatusMissing: 'Dieser Ablauf hat keinen aktiven Abschlussstatus',
                        chooseReviewer: 'Wähle eine prüfende Person, bevor du die Aufgabe abschließt',
                        parentProgressInconsistent: 'Der Fortschritt der übergeordneten Aufgabe ist inkonsistent',
                        unknownUser: 'Unbekannter Organisationsbenutzer',
                        unknownContact: 'Unbekannter Organisationskontakt',
                        invalidTeamAssignees:
                            'Teamaufgaben können nur Kontakten zugewiesen werden, die zu diesem Team gehören.',
                        duplicatePerson: 'Dieselbe Person kann nicht doppelt zugewiesen werden: {{duplicates}}',
                        duplicatePersonUser: '{{name}} (Benutzer)',
                        duplicatePersonContact: '{{name}} (Kontakt)',
                        unknownTeam: 'Unbekanntes Team',
                        unknownEvent: 'Unbekannte Veranstaltung',
                        unknownResource: 'Unbekannte Ressource',
                        unknownConversation: 'Unbekannte Konversation',
                        unknownProject: 'Unbekanntes oder archiviertes Projekt',
                        projectRequired:
                            'Wähle ein Projekt, wenn die Aufgabe keinem Team, keiner Veranstaltung, keiner Ressource und keiner Konversation zugeordnet ist',
                        projectWithContext: 'Nur Aufgaben ohne Bezug können einem Projekt zugeordnet werden',
                        unknownWorkflow: 'Unbekannter oder archivierter Aufgabenablauf',
                        statusTransitionNotAllowed: 'Dieser Statuswechsel ist im Ablauf nicht erlaubt',
                    },
                },
            },
        },
        entities: {
            common: {
                clientAppType: {
                    admin: 'Admin',
                    'connect-web': 'Vereins-App Web',
                    'connect-ios': 'Vereins-App iOS',
                    'connect-android': 'Vereins-App Android',
                    'portal-web': 'Portal Web',
                    'portal-ios': 'Portal iOS',
                    'portal-android': 'Portal Android',
                    embeddable: 'Einbettung',
                    root: 'Root',
                },
                mandate: {
                    entityName_one: 'Mandant',
                    entityName_other: 'Mandanten',
                },
                address: {
                    address: {
                        groupName: 'Adresse',
                        country: 'Land',
                        state: 'Bundesstaat',
                        zip: 'Postleitzahl',
                        city: 'Stadt',
                        addressLine: 'Straße und Hausnummer',
                        details1: 'weitere Empfängerangabe (z.B. Firma oder c/o)',
                        details2: 'weitere Empfängerangabe (z.B. Stockwerk)',
                    },
                },
                recipientType: {
                    business: 'Unternehmen',
                    person: 'Person',
                },
            },
            notification: {
                category: {
                    teams: 'Teams',
                    selfService: 'Self-Service',
                    network: 'Netzwerk',
                    'community.reply': 'Community-Antwort',
                    'feedback.comment': 'Feedback-Kommentar',
                    'feedback.tickets': 'Feedback-Tickets',
                    export: 'Export',
                    job: 'Auftrag',
                    review: 'Prüfungen',
                    'questionnaire.submission': 'Fragebogen-Einsendung',
                    'contact.resubmission': 'Kontakt-Wiedervorlage',
                    'contact.rule': 'Kontaktänderung',
                    'conversation.inbox': 'Posteingang',
                    todo: 'Aufgaben',
                    booking: 'Buchungen',
                    compensation: 'Aufwandsentschädigungen',
                    workHours: 'Arbeitsstunden',
                    donation: 'Spenden',
                    social: 'Social Media',
                },
            },
            work: {
                todoProject: {
                    groupName: 'Projekt',
                    name: 'Name',
                    color: 'Farbe',
                    colors: {
                        slate: 'Schiefer',
                        gray: 'Grau',
                        zinc: 'Zink',
                        neutral: 'Neutral',
                        stone: 'Stein',
                        red: 'Rot',
                        orange: 'Orange',
                        amber: 'Bernstein',
                        yellow: 'Gelb',
                        lime: 'Limette',
                        green: 'Grün',
                        emerald: 'Smaragd',
                        teal: 'Türkis',
                        cyan: 'Cyan',
                        sky: 'Himmelblau',
                        blue: 'Blau',
                        indigo: 'Indigo',
                        violet: 'Violett',
                        purple: 'Lila',
                        fuchsia: 'Fuchsia',
                        pink: 'Pink',
                        rose: 'Rosé',
                    },
                    description: 'Beschreibung',
                    openCount: 'Offene Aufgaben',
                    closedCount: 'Abgeschlossene Aufgaben',
                    createdAt: 'Erstellt am',
                    updatedAt: 'Aktualisiert am',
                    archivedAt: 'Archiviert am',
                },
                todoTopic: {
                    groupName: 'Thema',
                    name: 'Name',
                    description: 'Beschreibung',
                    openCount: 'Offene Aufgaben',
                    closedCount: 'Abgeschlossene Aufgaben',
                    createdAt: 'Erstellt am',
                    updatedAt: 'Aktualisiert am',
                    archivedAt: 'Archiviert am',
                },
                todoItem: {
                    item: {
                        groupName: 'Aufgabe',
                        title: 'Titel',
                        description: 'Beschreibung',
                        createdAt: 'Erstellt am',
                        updatedAt: 'Aktualisiert am',
                    },
                    workflow: {
                        project: 'Projekt',
                        groupName: 'Arbeitsablauf',
                        topic: 'Thema',
                        type: 'Typ',
                        status: 'Status',
                        statusRole: 'Statusrolle',
                        completion: 'Erledigung',
                        completionStates: {
                            open: 'Offen',
                            completed: 'Erledigt',
                        },
                        statusRoles: {
                            regular: 'Regulär',
                            review: 'Prüfung',
                            completed: 'Abgeschlossen',
                        },
                    },
                    people: {
                        groupName: 'Zuständige und Prüfung',
                        assignees: 'Zuständige',
                        assignedUsers: 'Zugewiesene Benutzer',
                        assignedContacts: 'Zugewiesene Kontakte',
                        reviewer: 'Prüfer',
                        reviewDecision: 'Prüfstatus',
                        reviewDecisions: {
                            none: 'Keine Prüfung',
                            pending: 'Ausstehend',
                            accepted: 'Angenommen',
                            rejected: 'Abgelehnt',
                            withdrawn: 'Zurückgezogen',
                        },
                    },
                    context: {
                        groupName: 'Bezug',
                        type: 'Bezugsart',
                        name: 'Bezug',
                        types: {
                            team: 'Team',
                            event: 'Veranstaltung',
                            resource: 'Ressource',
                            conversation: 'Konversation',
                        },
                    },
                    schedule: {
                        groupName: 'Zeitraum',
                        dueDate: 'Fällig am',
                        workCompletedAt: 'Eingereicht am',
                        completedAt: 'Abgeschlossen am',
                    },
                    activity: {
                        groupName: 'Aktivität',
                        tags: 'Schlagwörter',
                        attachmentCount: 'Anzahl der Anhänge',
                        attachmentCountInfo:
                            'Verwende 0 für Aufgaben ohne Anhänge und größer als 0 für Aufgaben mit Anhängen.',
                        commentsCount: 'Anzahl der Kommentare',
                        childrenCount: 'Anzahl der Unteraufgaben',
                        childrenCompletedCount: 'Abgeschlossene Unteraufgaben',
                    },
                },
            },
            organization: {
                organization: {
                    common: {
                        groupName: 'Allgemein',
                        shortId: 'Kurz-ID',
                        name: 'Name',
                        website: 'Website',
                        foundingYear: 'Gründungsjahr',
                        memberCount: 'Mitglieder',
                        childrenMemberCount: 'Mitglieder in Unterorganisationen',
                        userCount: 'Benutzer',
                        createdAt: 'Erstellt am',
                        language: 'Sprache',
                        address: 'Adresse',
                    },
                    classification: {
                        groupName: 'Klassifizierung',
                        type: 'Typ',
                        area: 'Bereich',
                        sports: 'Sportarten',
                    },
                    hierarchy: {
                        groupName: 'Hierarchie',
                        parentOrganizations: 'Übergeordnete Organisationen',
                        childrenCount: 'Anzahl Unterorganisationen',
                    },
                    subscriptions: {
                        groupName: 'Abonnements',
                        product: {
                            walletPasses: 'Wallet-Pässe',
                            outgoingMessagesArchive: 'Nachrichtenarchiv',
                            fileStorage: 'Dateispeicher',
                        },
                    },
                    billing: {
                        groupName: 'Abrechnung',
                        groupNameShort: 'Abr',
                        stripeCustomerId: 'Stripe-Kunden-ID',
                        products: 'Produkte',
                        productOptions: {
                            main: 'Mitglieder',
                            walletPasses: 'Digitale Ausweise',
                            outgoingMessagesArchive: 'Nachrichtenarchiv',
                            fileStorage: 'Dateispeicher',
                        },
                        revenue: {
                            standalonePayments: 'Einmalzahlungen',
                            lifetimeValue: 'Gesamtumsatz',
                            currentMrr: 'Aktueller MRR',
                            commissions: {
                                stripe: 'Stripe-Provisionen',
                                paypal: 'PayPal-Provisionen',
                            },
                        },
                        subscription: {
                            groupName: 'Abonnement',
                            groupNameShort: 'Abo',
                            id: 'Abonnement-ID',
                            status: 'Status',
                            isCustom: 'Individuelles Abonnement',
                            startDate: 'Startdatum',
                            trialEndDate: 'Ende des Testzeitraums',
                            cancelDate: 'Kündigungsdatum',
                            cancelReason: {
                                reason: 'Kündigungsgrund',
                                comment: 'Kommentar zur Kündigung',
                            },
                            cancelBehaviour: 'Verhalten bei Kündigung',
                            statusOptions: {
                                trialing: 'Testphase',
                                active: 'Aktiv',
                                pastDue: 'Zahlung überfällig',
                                unpaid: 'Unbezahlt',
                                willCancel: 'Kündigung vorgemerkt',
                                canceled: 'Gekündigt',
                            },
                            cancelReasonOptions: {
                                other: 'Sonstiges',
                                customer_service: 'Kundenservice',
                                low_quality: 'Mangelnde Qualität',
                                missing_features: 'Fehlende Funktionen',
                                switched_service: 'Anbieter gewechselt',
                                too_complex: 'Zu komplex',
                                too_expensive: 'Zu teuer',
                                unused: 'Nicht genutzt',
                                trial_expired: 'Testzeitraum abgelaufen',
                                first_payment_failed: 'Erste Zahlung fehlgeschlagen',
                            },
                            cancelBehaviourOptions: {
                                lockOrganization: 'Organisation sperren',
                                deleteOrganization: 'Organisation löschen',
                            },
                        },
                        accessLock: {
                            groupName: 'Zugangssperre',
                            groupNameShort: 'Sperre',
                            isLocked: 'Gesperrt',
                            adminLockedAt: 'Admin-Zugang gesperrt am',
                            publicLockedAt: 'Öffentlicher Zugang gesperrt am',
                            deletionAt: 'Löschdatum der Organisation',
                        },
                    },
                    integrations: {
                        groupName: 'Integrationen',
                        stripeAccount: 'Stripe-Konto',
                        paypalAccount: 'PayPal-Konto',
                        eventim: 'Eventim',
                        tixxId: 'Tixx-ID',
                    },
                    internal: {
                        groupName: 'Intern',
                        credit: 'Guthaben',
                        verified: 'Verifiziert',
                        onlineLetterSending: 'Online-Briefversand',
                        newCRMMode: 'Neuer CRM-Modus',
                        migrationStatus: 'Migrationsstatus',
                    },
                },
            },
            propertyFilter: {
                comparator: {
                    string: {
                        equals: 'Gleich',
                        notEquals: 'Ungleich',
                        contains: 'Enthält',
                        notContains: 'Enthält nicht',
                        startsWith: 'Beginnt mit',
                        endsWith: 'Endet mit',
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                        includes: 'Enthält irgendeins',
                        includesAll: 'Enthält alle',
                        notIncludes: 'Enthält nicht irgendeins',
                        notIncludesAll: 'Enthält keines',
                        empty: 'Leere liste',
                        notEmpty: 'Keine leere liste',
                    },
                    objectid: {
                        equals: 'Gleich',
                        notEquals: 'Ungleich',
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                        includes: 'Enthält irgendeins',
                        includesAll: 'Enthält alle',
                        notIncludes: 'Enthält nicht irgendeins',
                        notIncludesAll: 'Enthält keines',
                        empty: 'Leere liste',
                        notEmpty: 'Keine leere liste',
                    },
                    number: {
                        equals: 'Gleich',
                        notEquals: 'Ungleich',
                        greaterThan: 'Größer als',
                        greaterEquals: 'Größer gleich',
                        lessThan: 'Kleiner als',
                        lessEquals: 'Kleiner gleich',
                        between: 'Zwischen',
                        notBetween: 'Nicht zwischen',
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                    },
                    date: {
                        daysAgo: 'Tagen',
                        exactlyDaysAgo: 'Vor exakt',
                        moreThanDaysAgo: 'Vor mehr als',
                        lessThanDaysAgo: 'Vor weniger als',
                        moment: 'Relativer Zeitpunkt',
                        momentValue: {
                            beforeToday: 'Vor heute',
                            beforeOrAtToday: 'Vor oder heute',
                            afterToday: 'Nach heute',
                            afterOrAtToday: 'Nach oder heute',
                        },
                        equals: 'Am',
                        notEquals: 'Nicht am',
                        greaterThan: 'Nach dem',
                        greaterEquals: 'Nach oder am',
                        lessThan: 'Vor dem',
                        lessEquals: 'Vor oder am',
                        between: 'Zwischen',
                        notBetween: 'Nicht zwischen',
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                    },
                    bool: {
                        isTrue: 'Ist Wahr',
                        isFalse: 'Ist Falsch',
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                    },
                    any: {
                        exists: 'Ist gesetzt',
                        notExists: 'Nicht gesetzt',
                    },
                },
            },
            user: {
                user: {
                    common: {
                        groupName: 'Personen',
                        userId: 'Benutzer-ID',
                        name: 'Name',
                        email: 'E-Mail',
                        language: 'Sprache',
                        bio: 'Biografie',
                        pushToken: 'Push-Token',
                        organizationsCount: 'Anzahl Organisationen',
                        globalPermissions: 'Globale Berechtigungen',
                        createdAt: 'Erstellt am',
                        lastSeen: 'Zuletzt gesehen',
                    },
                    organization: {
                        type: 'Art',
                        accessLevels: {
                            owner: 'Eigentümer',
                            organization: 'Verwaltung',
                            contact: 'Vereins-App',
                            guest: 'Gast',
                        },
                        portal: 'Portal',
                        organizationPermissions: 'Verwaltung',
                        role: 'Rolle',
                        contacts: 'Kontakte',
                        groups: {
                            user: 'Benutzer',
                            access: 'Zugriff',
                            lastSeenAt: 'Zuletzt gesehen am',
                        },
                    },
                    globalPermissions: {
                        'organization.read': 'Organisation lesen',
                        'organization.billing': 'Organisation Abrechnung',
                        'organization.write': 'Organisation bearbeiten',
                        'user.read': 'Benutzer lesen',
                        'user.write': 'Benutzer bearbeiten',
                        'auditLog.read': 'Audit-Log lesen',
                        'community.admin': 'Community administrieren',
                        'feedback.admin': 'Feedback administrieren',
                        'customer.reportType.admin': 'Kundenberichtstypen administrieren',
                        'customer.report': 'Kundenbericht',
                        'customer.report.admin': 'Kundenbericht administrieren',
                        'customer.report.billing': 'Kundenberichte abrechnen',
                    },
                },
            },
            dispatchDocument: {
                createdAt: 'Datum',
                sendStatus: 'Status',
                receipt: 'Beleg',
                recipient: {
                    name: 'Empfänger',
                    email: 'E-Mail',
                    address: 'Adresse',
                },
                sendMethod: 'Versandart',
                sentAt: 'Gesendet am',
                openedAt: 'Geöffnet am',
                sendPrice: 'Kosten',
                type: 'Typ',
            },
            dispatchDocumentType: {
                receipt: 'Beleg',
                donation: 'Spende',
                proof: 'Nachweis',
                certificate: 'Zertifikat',
                pass: 'Pass',
                booking: 'Buchung',
                message: 'Nachricht',
                mailing: 'Mailing',
                applicationForm: 'Antragsformular',
                donationForm: 'Spendenformular',
                contactRule: 'Automatisierung',
                questionnaireForm: 'Fragebogen',
            },
            dispatchDocumentTypeViewName: {
                receipt: 'Ausgehende Rechnungen',
                donation: 'Spendenbestätigungen',
                proof: 'Nachweis-Anfragen & Bestätigungen',
                certificate: 'Zertifikatsbestätigungen',
                pass: 'Ausgestellte digitale Pässe',
                booking: 'Buchung',
                mailing: 'Mailings',
                message: 'Nachrichten',
                applicationForm: 'Antragsformulare',
                donationForm: 'Spendenformulare',
                contactRule: 'Automatisierungen',
                questionnaireForm: 'Fragebögen',
            },
            misc: {
                integration: {
                    businessCentral: {
                        customerNumberRequired: 'Eine Kundennummer-Zuordnung ist erforderlich',
                    },
                },
                questionnaireForm: {
                    statusValues: {
                        draft: 'Entwurf',
                        active: 'Aktiv',
                        paused: 'Pausiert',
                        closed: 'Abgeschlossen',
                    },
                },
                questionnaireSubmission: {
                    properties: {
                        submission: {
                            groupName: 'Einreichung',
                            createdAt: 'Eingereicht am',
                            questionnaireForm: 'Fragebogen',
                            status: 'Status',
                            emailConfirmed: 'E-Mail bestätigt',
                            dispatchStatus: 'Versandstatus',
                        },
                    },
                },
            },
            dispatchDocumentSendMethod: {
                none: 'Kein Versand',
                auto: 'Auto-Versand (E-Mail -> Brief)',
                email: 'Versand als E-Mail',
                postal: 'Versand als Brief',
            },
            dispatchDocumentSendStatus: {
                none: {
                    status: 'Wird nicht versendet',
                    description: 'Das Dokument wurde manuell als nicht zu versenden markiert',
                },
                manual: {
                    status: 'Manueller Briefversand',
                    description:
                        'Das Dokument muss ausgedruckt und per Post von dir versendet werden. Nachdem du das Dokument per Post versendet hast, kannst du es als versendet markieren.',
                },
                dispatching: {
                    status: 'Wird versendet',
                    description: 'Das Dokument wartet in der Warteschlange auf den Versand',
                },
                dispatched: {
                    status: 'Im Versand',
                    description: 'Das Dokument wurde an den Verarbeiter übergeben',
                },
                sent: {
                    status: 'Wurde versendet',
                    description: 'Das Dokument wurde vom Verarbeiter an den Empfänger versendet',
                },
                opened: {
                    status: 'Wurde geöffnet',
                    description: 'Das Dokument wurde vom Empfänger geöffnet',
                },
                failed: {
                    status: 'Fehlgeschlagen',
                    description: 'Das Dokument konnte nicht versendet werden',
                },
            },
            dispatchDocumentSendError: {
                MAILING_UNAVAILABLE: {
                    status: 'Rundschreiben nicht verfügbar',
                    description:
                        'Das Rundschreiben ist nicht mehr zum Versand verfügbar. Es wurde keine E-Mail übermittelt.',
                },
                MAILING_RECIPIENT_UNAVAILABLE: {
                    status: 'Empfänger nicht verfügbar',
                    description:
                        'Der ursprüngliche Kontakt des Rundschreibens ist nicht mehr verfügbar. Es wurde keine E-Mail übermittelt.',
                },
                MAILING_UNSUBSCRIBED: {
                    status: 'Abgemeldet',
                    description:
                        'Diese E-Mail-Adresse wurde von diesen Rundschreiben abgemeldet. Es wurde keine E-Mail übermittelt.',
                },
                NO_CREDITS_ENOUGH: {
                    status: 'Unzureichendes Guthaben',
                    description:
                        'Du hast nicht genügend Guthaben um das Dokument online zu versenden. Bitte lade Guthaben auf dein Konto, danach kannst du alle Dokumente die deswegen nicht versendet wurden erneut versenden.',
                },
            },
            donation: {
                frequency: {
                    oneTime: 'Einmalig',
                    recurring: 'Regelmäßig',
                },
                donationFormSubmissionStatus: {
                    unread: 'Neu',
                    pending: 'In Bearbeitung',
                    approved: 'Angenommen',
                    rejected: 'Abgelehnt',
                },
                donationType: {
                    cash: 'Geldzuwendung',
                    membershipFee: 'Mitgliedsbeitrag',
                    assetStock: 'Vermögensstock',
                    refund: 'Aufwandsverzicht',
                    nonCash: 'Sachspende',
                },
                confirmationStatus: {
                    pending: 'Offen',
                    notRequired: 'Ohne Bestätigung',
                    created: 'Bestätigt',
                    thanked: 'Dankesbrief',
                },
                properties: {
                    donation: {
                        groupName: 'Spende',
                        number: 'Nummer',
                        donatorName: 'Spender',
                        donatorAccount: 'Debitorenkonto',
                        donatorEmail: 'E-Mail',
                        donatorAddress: 'Adresse',
                        totalValue: 'Gesamtbetrag',
                        confirmationStatus: 'Bestätigung',
                        confirmationSendMethod: 'Versandart',
                        receiptNumbers: 'Belege',
                        revokedAt: 'Storniert am',
                    },
                    positions: {
                        groupName: 'Spende',
                        date: 'Datum',
                        type: 'Art',
                        value: 'Betrag',
                        description: 'Beschreibung',
                        receiptNumber: 'Buchungsbeleg',
                        debitAccount: 'Eingang auf Konto',
                        account: 'Konto',
                        costCenter1: 'Kostenstelle 1',
                        costCenter2: 'Kostenstelle 2',
                    },
                },
                donatorProperties: {
                    groupName: 'Spender',
                    name: 'Spender',
                    account: 'Debitorenkonto',
                    totalValue: 'Summe',
                    donationCount: 'Anzahl Spenden',
                    averageValue: 'Durchschnitt',
                    lastDonationDate: 'Letzte Spende',
                },
                sampleTexts: {
                    donatorName: 'Max Mustermann',
                    positionDescription: 'Spende für die Jugendarbeit',
                    details1: 'Empfänger Details 1',
                    details2: 'Empfänger Details 2',
                    addressLine: 'Musterstraße 123',
                    city: 'Stadt',
                    zip: '12345',
                },
            },
            austrianDonationSubmission: {
                reportingYear: 'Meldejahr',
                messageRefID: 'Referenz',
                exportDate: 'Exportdatum',
                exportFile: 'Exportdatei',
                returnFile: 'Rückmeldedatei',
                submissionType: 'Übermittlungsart',
                ignoreDebtorsMissingBirthDate: 'Fehlende Geburtstage ignorieren',
                ignoreSubmitedDebtors: 'Eingereichte Debitoren ignorieren',
                submissionTypeValues: {
                    E: 'Erstübermittlung',
                    A: 'Korrekturübermittlung',
                    S: 'Stornierung',
                },
                status: 'Status',
                statusValues: {
                    error: 'Fehler',
                    donator_creating: 'vbPK wird erstellt',
                    donator_available: 'vbPK bereit',
                    donator_returnedProcessing: 'vbPK wird geprüft',
                    donator_returnedError: 'vbPK-Fehler',
                    donator_returnedPartialSuccess: 'vbPK teilweise ok',
                    donator_returnedSuccess: 'vbPK ok',
                    donations_creating: 'Übermittlung wird erstellt',
                    donations_available: 'Übermittlung bereit',
                    donations_returnedProcessing: 'Übermittlung wird geprüft',
                    donations_returnedError: 'Übermittlungsfehler',
                    donations_returnedPartialSuccess: 'Übermittlung teilweise ok',
                    donations_returnedSuccess: 'Übermittlung ok',
                },
                errorCodeValues: {
                    'ERR-F001': 'Die Fastnr_Fon_Tn muss gültig, 9-stellig und vorhanden sein.',
                    'ERR-F003':
                        'Die im Feld Fastnr_Fon_Tn eingegebene Steuernummer ist nicht dem FinanzOnline-Teilnehmer zugeordnet.',
                    'ERR-F008': 'Die Fastnr_Org muss gültig, 9-stellig und vorhanden sein.',
                    'ERR-F009': 'Es liegt keine Berechtigung für die Übermittlung vor (Fastnr_Fon_Tn).',
                    'ERR-F010': 'Es liegt keine Berechtigung für das übermittelte Jahr vor (Fastnr_Org).',
                    'ERR-F011':
                        'Das bei der Übermittlung verwendete Spendenkürzel (im FeldÜbermittlungsart) stimmt nicht mit der beim Finanzamt angemerkten Art der Spendenorganisation überein.',
                    'ERR-F012':
                        'Der Dienstleister ist nicht berechtigt, für die Organisation eine Übermittlung vorzunehmen.',
                    'ERR-F013':
                        'Der Zeitraum darf nicht in der Zukunft und nicht im aktuellen Jahr liegen. Erstmaliger Zeitraum ist 2017.',
                },
            },
            finApiBankConnection: {
                defaultName: 'Bank-Verbindung #{{connectionId}}',
            },
            crm: {
                application: {
                    submission: {
                        status: {
                            unread: 'Ungelesen',
                            pending: 'Ausstehend',
                            approved: 'Genehmigt',
                            rejected: 'Abgelehnt',
                        },
                        reviewStatus: {
                            none: 'Keine Prüfung',
                            pending: 'In Prüfung',
                            accepted: 'Akzeptiert',
                            rejected: 'Abgelehnt',
                        },
                        emailConfirmed: {
                            pending: 'Nicht bestätigt',
                            confirmed: 'Bestätigt',
                        },
                        properties: {
                            submission: {
                                groupName: 'Einreichung',
                                createdAt: 'Eingereicht am',
                                applicationForm: 'Antragsformular',
                                status: 'Status',
                                emailConfirmed: 'E-Mail bestätigt',
                                dispatchStatus: 'Versandstatus',
                                reviewStatus: 'Prüfstatus',
                                reviewer: 'Prüfer',
                                reviewers: 'Prüfende',
                            },
                        },
                    },
                },
                contact: {
                    contactType: {
                        member: {
                            single: 'Mitglied',
                            multi: 'Mitglieder',
                            contactNumber: 'Mitgliedsnummer',
                        },
                        address: {
                            single: 'Adresse',
                            multi: 'Adressen',
                            contactNumber: 'Adressnummer',
                        },
                        customer: {
                            single: 'Kunde',
                            multi: 'Kunden',
                            contactNumber: 'Kundennummer',
                        },
                        sponsor: {
                            single: 'Sponsor',
                            multi: 'Sponsoren',
                            contactNumber: 'Sponsornummer',
                        },
                        contributor: {
                            single: 'Ehrenamtliche',
                            multi: 'Ehrenamtliche',
                            contactNumber: 'Ehrenamtsnummer',
                        },
                        employee: {
                            single: 'Mitarbeiter',
                            multi: 'Mitarbeiter',
                            contactNumber: 'Mitarbeiternummer',
                        },
                        supplier: {
                            single: 'Lieferant',
                            multi: 'Lieferanten',
                            contactNumber: 'Lieferantennummer',
                        },
                    },
                    contactSegment: {
                        defaultSegments: {
                            members: {
                                name: 'Aktuelle Mitglieder',
                                hint: 'Alle Mitglieder, die bereits eingetreten & noch nicht ausgetreten sind',
                            },
                            futureMembers: {
                                name: 'Zukünftige Mitglieder',
                                hint: 'Alle Mitglieder, die in Zukunft eintreten werden',
                            },
                            resignedMembers: {
                                name: 'Ausgetretene Mitglieder',
                                hint: 'Alle Mitglieder, die bereits ausgetreten sind',
                            },
                        },
                    },
                    contactGender: {
                        female: 'Weiblich',
                        male: 'Männlich',
                        diverse: 'Divers',
                        none: 'Keine Angabe',
                    },
                    contactInstitutionType: {
                        business: 'Unternehmen',
                        club: 'Verein',
                        association: 'Verband',
                        city: 'Stadt',
                        other: 'Anderes',
                    },
                    contact: {
                        common: {
                            groupName: 'Allgemein',
                            type: 'Art',
                            name: 'Name',
                            createdAt: 'Erstellt am',
                            updatedAt: 'Aktualisiert am',
                            language: 'Sprache',
                            tags: 'Tags',
                            activeDepartments: 'Aktive Abteilungen',
                            segments: 'Segmente',
                        },
                        user: {
                            groupName: 'Benutzer',
                            actor: {
                                userId: 'Hat Benutzerkonto',
                                name: 'Name',
                            },
                            claimedAt: 'Verknüpft am',
                        },
                        connect: {
                            groupName: 'Vereins-App',
                            joinedAt: 'Netzwerk: Beigetreten am',
                            reviewedAt: 'Netzwerk: Überprüft am',
                            remindedAt: 'Netzwerk: Erinnert am',
                        },
                        person: {
                            groupName: 'Person',
                            salutation: 'Anrede',
                            title: 'Titel',
                            gender: 'Geschlecht',
                            firstName: 'Vorname',
                            lastName: 'Nachname',
                            birthdate: 'Geburtsdatum',
                            yearOfBirth: 'Geburtsjahr',
                            age: 'Alter',
                            institutionName: 'Organisation',
                        },
                        institution: {
                            groupName: 'Organisation',
                            type: 'Art',
                            name: 'Name',
                        },
                        communication: {
                            groupName: 'Kommunikation',
                            email: 'Email',
                            phone: 'Telefon',
                            mobilePhone: 'Mobiltelefon',
                            mailing: 'Erhält Mailings',
                        },
                        eventim: {
                            groupName: 'Eventim',
                            tixxId: 'Tixx-ID',
                        },
                        alternativeCommunications: {
                            groupName: 'Alternative Kommunikation',
                            name: 'Name des Kontakts',
                            description: 'Beschreibung des Kontakts',
                        },
                        departments: {
                            groupName: 'Abteilungen',
                            department: 'Abteilung',
                            name: 'Name',
                            entryAt: 'Eintritt',
                            exitAt: 'Austritt',
                        },
                        groups: {
                            groupName: 'Gruppen',
                            group: 'Gruppe',
                            type: 'Gruppentyp',
                            name: 'Name',
                            number: 'Nummer',
                            contacts: 'Anzahl der Kontakte',
                            contactSegmentCounters: 'Segment-Kontaktzähler',
                        },
                        proofs: {
                            groupName: 'Nachweise',
                            type: 'Nachweistyp',
                            typeName: 'Art des Nachweises',
                            status: 'Status',
                        },
                        certificates: {
                            groupName: 'Zertifikate',
                            typeLabel: 'Zertifikatstyp',
                            type: {
                                sampleTexts: {
                                    name: 'Trainer/Leiterlizenz',
                                },
                            },
                            typeName: 'Art des Zertifikats',
                            status: 'Status',
                        },
                        member: {
                            groupName: 'Mitgliedschaft',
                            entryAt: 'Eintritt',
                            exitAt: 'Austritt',
                            terminatedAt: 'Gekündigt am',
                            terminationReason: 'Kündigungsgrund',
                            membershipDuration: 'Mitgliedschaftsdauer',
                        },
                        employee: {
                            groupName: 'Mitarbeiter',
                            type: 'Art',
                            typeValue: {
                                internal: 'Intern',
                                external: 'Extern',
                            },
                            status: 'Status',
                            statusValue: {
                                active: 'Aktiv',
                                inactive: 'Inaktiv',
                                onboarding: 'Onboarding',
                                leave: 'Verlassen',
                            },
                            position: 'Position',
                            probationStartAt: 'Probezeitstart am',
                            probationEndAt: 'Probezeitende am',
                            employmentStartAt: 'Einstellung am',
                            employmentEndAt: 'Entlassung am',
                            contractEndAt: 'Vertragsende am',
                            terminatedAt: 'Gekündigt am',
                            lastWorkingDayAt: 'Letzter Arbeitstag',
                        },
                        contributor: {
                            groupName: 'Ehrenamtliche',
                            status: 'Status',
                            statusValue: {
                                active: 'Aktiv',
                                inactive: 'Inaktiv',
                                onboarding: 'Onboarding',
                                paused: 'Pausiert',
                            },
                            roleTitle: 'Rolle',
                            contributionStartAt: 'Ehrenamtsbeginn',
                            contributionEndAt: 'Ehrenamtsende',
                        },
                        debtor: {
                            groupName: 'Debitor',
                            account: 'Konto',
                            name: 'Name',
                            receiptSendMethod: 'Belegversand',
                            paymentMethodType: 'Zahlungsmethode',
                            sepaDirectDebitMandate: {
                                iban: 'IBAN',
                                ibanMasked: 'IBAN (maskiert)',
                                bic: 'BIC',
                                sepaMandateId: 'Mandats-ID',
                                sepaMandateSignatureDate: 'Unterschriftsdatum',
                                accountHolderName: 'Abweichender Kontoinhaber',
                            },
                        },
                        creditor: {
                            groupName: 'Kreditor',
                            account: 'Konto',
                            name: 'Name',
                        },
                        contracts: {
                            groupName: 'Verträge',
                            contract: 'Vertrag',
                            startAt: 'Start',
                            endAt: 'Ende',
                            pauseStartAt: 'Pausenbeginn',
                            pauseEndAt: 'Pausenende',
                            lastPaidPeriodStartAt: 'Letzter bezahlter Zeitraum › Beginn',
                            lastPaidPeriodEndAt: 'Letzter bezahlter Zeitraum › Ende',
                            lastCollectionAdjustments: 'Letzte Abrechnung › Anpassungen',
                            plan: 'Plan',
                            paymentInterval: 'Zahlungsintervall',
                        },
                        sampleTexts: {
                            organizationName: 'Campai',
                            firstName: 'Max',
                            lastName: 'Mustermann',
                            institutionName: 'Campai',
                            salutation: 'Herr',
                            title: 'Dr.',
                            email: 'max.mustermann@example.com',
                            phone: '+49 123 456789',
                            mobilePhone: '+49 123 4567890',
                            details1: 'c/o Frau Mustermann',
                            details2: 'Wohnung 4',
                            addressLine: 'Musterstraße 123',
                            city: 'Berlin',
                            state: 'Berlin',
                            zip: '12345',
                            greetingsPerson: 'Sehr geehrter Herr Mustermann',
                        },
                    },
                    customFields: 'Benutzerdefinierte Felder',
                    otherGroup: 'Sonstiges',
                },
                group: {
                    group: {
                        group: {
                            groupName: 'Gruppe',
                            number: 'Nummer',
                            contacts: 'Kontakte',
                            name: 'Name',
                            description: 'Beschreibung',
                            segments: 'Segmente',
                            contactSegmentCounters: 'Kontaktzähler des Segments',
                        },
                    },
                    membership: {
                        member: {
                            groupName: 'Mitglied',
                            contactName: 'Kontakt',
                            createdAt: 'Hinzugefügt am',
                        },
                    },
                },
                contract: {
                    contract: {
                        contractStatus: {
                            collect: 'Einzuziehen',
                            active: 'Aktiv',
                            upcoming: 'Anstehend',
                            paused: 'Pausiert',
                            termination: 'Gekündigt',
                            ended: 'Beendet',
                            failed: 'Fehlgeschlagen',
                            collecting: 'Wird abgerechnet',
                        },
                        contactDebtor: {
                            groupName: 'Kontakt / Debitor',
                            contactName: 'Kontakt',
                            debtorName: 'Debitor',
                            debtorAccount: 'Konto',
                            debtorPaymentMethodType: 'Zahlungsmethode',
                        },
                        contract: {
                            groupName: 'Vertrag',
                            startAt: 'Start',
                            endAt: 'Ende',
                            terminatedAt: 'Gekündigt am',
                            pauseStartAt: 'Pausenbeginn',
                            pauseEndAt: 'Pausenende',
                            createdAt: 'Erstellt am',
                            fixedAmount: 'Festbetrag',
                            totalCollectedAmount: 'Gesamtbetrag',
                        },
                        plan: {
                            groupName: 'Vertragsart',
                            name: 'Name',
                            tags: 'Tags',
                            interval: 'Intervall',
                        },
                        collection: {
                            collectAt: 'Einzuziehen am',
                            periodStart: 'Von',
                            periodEnd: 'Bis',
                            amount: 'Betrag',
                            description: 'Beschreibung',
                            voucherName: 'Gutschein',
                            voucherAmount: 'Betrag',
                            counter: 'Zähler',
                        },
                        nextCollection: {
                            groupName: 'Nächste Einziehung',
                            contractCollectionId: 'Vertragsabrechnung',
                        },
                        lastCollection: {
                            groupName: 'Letzte Einziehung',
                        },
                    },
                    collection: {
                        collection: {
                            groupName: 'Abrechnung',
                            createdAt: 'Abgerechnet am',
                            status: 'Status',
                            nextAction: 'Nächste Aktion',
                            contractCounter: 'Verträge',
                            statusValue: {
                                collecting: 'Wird abgerechnet',
                                draft: 'Entwurf',
                                discarded: 'Verworfen',
                                completed: 'Abgeschlossen',
                                sent: 'Gesendet',
                                partialPaid: 'Teilweise bezahlt',
                                paid: 'Bezahlt',
                            },
                            statusInfo: {
                                collecting: 'Die Verträge werden abgerechnet',
                                draft: 'Die Belege werden abgeschlossen und es gibt mindestens einen Entwurfsbeleg',
                                discarded:
                                    'Dieser Abrechnungslauf enthält keine Belege mehr. Es liegen keine fehlgeschlagenen Vertragsabrechnungen vor.',
                                completed: 'Die Belege sind abgeschlossen',
                                sent: 'Die Belege wurden gesendet',
                                partialPaid: 'Die Zahlungen werden ausgeführt',
                                paid: 'Die Zahlungen sind abgeschlossen',
                            },
                            nextActionValue: {
                                complete: 'Belege abschließen',
                                send: 'Belege senden',
                                collectDirectDebits: 'Lastschriften einziehen',
                                collectOnlineDebits: 'Online-Einzüge einziehen',
                                charge: 'Offenen Betrag belasten',
                            },
                        },
                        receiptCounters: {
                            groupName: 'Beleg',
                            total: 'Gesamt',
                            draft: 'Entwurf',
                            completed: 'Abgeschlossen',
                            sent: 'Gesendet',
                        },
                        amounts: {
                            groupName: 'Beträge',
                            totalAmount: 'Gesamt',
                            openAmount: 'Offen',
                            paidAmount: 'Bezahlt',
                        },
                    },
                },
                plan: {
                    period: {
                        interval: {
                            month: 'Monatlich',
                            quarter: 'Quartalsweise',
                            halfYear: 'Halbjährlich',
                            year: 'Jährlich',
                            custom: 'Benutzerdefiniert',
                            flexible: 'Flexibel',
                        },
                        mode: {
                            interval: 'Nach Intervall',
                            range: 'Nach Zeitraum',
                        },
                    },
                    collection: {
                        period: {
                            start: 'Start',
                            end: 'Ende',
                        },
                        relative: {
                            at: 'Keine',
                            weekStart: 'Wochenstart',
                            weekEnd: 'Wochenende',
                            monthStart: 'Monatsstart',
                            monthEnd: 'Monatende',
                            quarterStart: 'Quartalsstart',
                            quarterEnd: 'Quartalende',
                            halfYearStart: 'Halbjahresstart',
                            halfYearEnd: 'Halbjahresende',
                            yearStart: 'Jahresstart',
                            yearEnd: 'Jahresende',
                        },
                        shiftMode: {
                            before: 'Vor',
                            after: 'Nach',
                        },
                        absoluteAnchor: {
                            interval: 'Intervall',
                            period: 'Zeitraum',
                        },
                        deviantMode: {
                            before: 'Vor Vertragsbeginn',
                            after: 'Nach Vertragsbeginn',
                            beforeIntervalStart: 'Vor Intervallbeginn',
                            afterIntervalStart: 'Nach Intervallbeginn',
                            beforeIntervalEnd: 'Vor Intervallende',
                            afterIntervalEnd: 'Nach Intervallende',
                            immediate: 'Sofort',
                            next: 'Erst das nächste Intervall abrechnen',
                        },
                    },
                    planType: {
                        membershipFee: 'Mitgliedsbeitrag',
                        donation: 'Spende',
                        contract: 'Sonstiger Vertrag',
                    },
                    planChargeValueOperator: {
                        set: 'Setze Betrag',
                        add: 'Addiere Betrag',
                        subtract: 'Subtrahiere Betrag',
                        addPercentage: 'Addiere Prozent',
                        subtractPercentage: 'Subtrahiere Prozent',
                        multiplyBy: 'Multipliziere mit Betrag',
                        divideBy: 'Dividiere durch Betrag',
                    },
                    planChargeRelativeDate: {
                        periodStart: 'Relativ zum Beginn des Abrechnungszeitraums',
                        periodEnd: 'Relativ zum Ende des Abrechnungszeitraums',
                        periodCenter: 'Relativ zum Mitte des Abrechnungszeitraums',
                        collectAt: 'Relativ zum Abrechnungsdatum',
                    },
                    planChargeAmountProRataMode: {
                        start: 'Am Anfang',
                        end: 'Am Ende',
                        startEnd: 'Am Anfang und Ende',
                    },
                    plan: {
                        plan: {
                            groupName: 'Vertragsart',
                            name: 'Name',
                            tags: 'Tags',
                        },
                        period: {
                            groupName: 'Zeitraum',
                            interval: 'Intervall',
                            mode: 'Modus',
                            count: 'Stoppt nach',
                        },
                        collection: {
                            groupName: 'Einzug',
                            period: 'Abrechnungszeitraum',
                            relative: 'Relativ',
                            shiftMode: 'Modus',
                            shiftTimeValue: 'Zeit',
                            shiftTimeUnit: 'Einheit',
                            deviantMode: 'Abweichender Modus',
                            deviantTimeValue: 'Abweichende Zeit',
                            deviantTimeUnit: 'Abweichende Einheit',
                            absoluteDate: 'Fester Tag',
                            absoluteMonth: 'Fester Monat',
                            absoluteAnchor: 'Fest innerhalb von',
                            daysToDue: 'Tage zur Fälligkeit',
                        },
                        charge: {
                            groupName: 'Abrechnung',
                            amount: {
                                price: 'Preis',
                                customField: 'Benutzerdefiniertes Feld',
                                operator: 'Operator',
                                description: 'Beschreibung',
                                proRataDivisor: 'Pro-rata',
                            },
                            options: {
                                propertyName: 'Optionen',
                                price: 'Preis',
                                customField: 'Benutzerdefiniertes Feld',
                                operator: 'Operator',
                                description: 'Beschreibung',
                            },
                            adjustments: {
                                propertyName: 'Anpassungen',
                                operator: 'Operator',
                                amount: 'Betrag',
                            },
                        },
                        billing: {
                            groupName: 'Rechnungsstellung',
                            sepaDirectDebitPurpose: 'Lastschrift Verwendungszweck',
                            deleteContractAfterEnd: 'Vertrag nach Ende löschen',
                        },
                    },
                },
                proofType: {
                    validityModes: {
                        oneTime: 'Immer gültig',
                        byDate: 'Bis zu einem bestimmten Datum',
                        byInterval: 'Für ein bestimmtes Intervall',
                    },
                    validity: {
                        dueBeforeDaysMustBeGreaterThanOverdueBeforeDays:
                            'Tage vor Fälligkeit müssen größer sein als Tage vor Überfälligkeit',
                    },
                },
                proof: {
                    proofStatus: {
                        required: 'Benötigt',
                        requested: 'Angefordert',
                        confirmed: 'Bestätigt',
                        due: 'Fällig',
                        overdue: 'Überfällig',
                        expired: 'Abgelaufen',
                    },
                    proof: {
                        proof: {
                            groupName: 'Nachweis',
                            groupPath: 'Nachweis',
                            status: 'Status',
                            typeName: 'Art',
                            contactName: 'Kontakt',
                            validTo: 'Gültig bis',
                            requestDispatchStatusSendStatus: 'Anforderungsstatus',
                            confirmationDispatchStatusSendStatus: 'Bestätigungsstatus',
                        },
                        confirmation: {
                            groupName: 'Bestätigung',
                            groupPath: 'Bestätigung',
                            issuedAt: 'Erstellt am',
                            issuingAuthority: 'Ersteller',
                            reviewedAt: 'Überprüft am',
                            reviewedBy: 'Überprüft von',
                            notes: 'Notizen',
                            documents: 'Dokumente',
                        },
                    },
                },
                certificate: {
                    certificateStatus: {
                        valid: 'Gültig',
                        willExpire: 'Wird abgelaufen',
                        expired: 'Abgelaufen',
                    },
                    certificate: {
                        certificate: {
                            groupName: 'Zertifikat',
                            groupPath: 'Zertifikat',
                            status: 'Status',
                            typeName: 'Art',
                            contactName: 'Kontakt',
                            validTo: 'Gültig bis',
                            issuedAt: 'Erstellt am',
                            issuedBy: 'Ersteller',
                            documents: 'Dokumente',
                            confirmationDispatchStatusSendStatus: 'Bestätigungsstatus',
                            notes: 'Notizen',
                        },
                    },
                },
                crmSettings: {
                    stockReport: {
                        typeValues: {
                            singleSport: 'Einzelverein',
                            multipleSports: 'Mehrspartenverein',
                        },
                        clubNumber: 'Vereinnummer',
                        contactPerson: 'Kontaktperson',
                        associationNumber: 'Verbandsnummer',
                        sportNumber: 'Sportnummer',
                    },
                    contactNumberFormats: {
                        typePrefix: {
                            member: 'MG',
                            address: 'AD',
                            employee: 'MT',
                            contributor: 'EA',
                            customer: 'KU',
                            sponsor: 'SP',
                            supplier: 'LF',
                        },
                    },
                },
                passes: {
                    passTemplate: {
                        statistics: {
                            pending: 'Ausstehend',
                            installed: 'Installiert',
                            uninstalled: 'Deinstalliert',
                            printed: 'Gedruckt',
                        },
                    },
                    issuedPass: {
                        common: {
                            groupName: 'Allgemein',
                            passTemplateName: 'Passvorlage',
                            passTemplatePrintable: 'Druckbar',
                        },
                        holder: {
                            groupName: 'Inhaber',
                            name: 'Name',
                            email: 'Email',
                        },
                        status: {
                            groupName: 'Status',
                            dispatch: 'Versand',
                            installation: 'Installation',
                            printing: 'Druckstatus',
                        },
                        digitalCard: {
                            groupName: 'Digitale Karte',
                        },
                        printedCard: {
                            groupName: 'Gedruckte Karte',
                        },
                        printExport: {
                            issuedPass: 'Ausgegebener Pass',
                            barcode: 'Interner Barcode',
                            contact: 'Kontakt',
                        },
                        dispatchStatus: 'Versandstatus',
                        dispatchStatusValue: {
                            scheduled: 'Geplant',
                            scheduledInfo: 'Die E-Mail wird zum geplanten Zeitpunkt an den Inhaber versendet',
                            none: 'Wird nicht versendet',
                            noneInfo: 'Die E-Mail wird nicht versendet, weil der Inhaber keine E-Mail-Adresse hat',
                            scheduledTo: 'Geplant zum',
                        },
                        installationStatus: 'Installationsstatus',
                        installationStatusValue: {
                            pending: 'Ausstehend',
                            installed: 'Installiert',
                            uninstalled: 'Deinstalliert',
                        },
                        printStatus: 'Druckstatus',
                        printStatusValue: {
                            pending: 'Ausstehend',
                            printed: 'Gedruckt',
                        },
                        revokedAt: 'Storniert am',
                        revokedBy: 'Storniert von',
                        createdAt: 'Erstellt am',
                        createdBy: 'Erstellt von',
                        printedAt: 'Gedruckt am',
                        printedBy: 'Gedruckt von',
                        receiptNumber: 'Belegnummer',
                        validFrom: 'Gültig ab',
                        validTo: 'Gültig bis',
                        validityStatus: 'Gültigkeit',
                        validityStatusValue: {
                            valid: 'Gültig',
                            validInfo: 'Der Pass ist innerhalb seiner Gültigkeit',
                            invalid: 'Ungültig',
                            invalidInfo: 'Der Pass ist außerhalb seiner Gültigkeit',
                            upcoming: 'Anstehend',
                            upcomingInfo: 'Der Pass ist noch nicht innerhalb seiner Gültigkeit',
                            revoked: 'Storniert',
                            revokedInfo:
                                'Der Pass wurde manuell oder automatisch aufgrund von Kontaktsektionen storniert',
                        },
                    },
                },
                conversation: {
                    mailboxValues: {
                        inbox: 'Eingang',
                        spam: 'Spam',
                        deleted: 'Gelöscht',
                    },
                },
                conversationMessage: {
                    mailStatusValues: {
                        pending: 'Ausstehend',
                        delivered: 'Zugestellt',
                        error: 'Fehlgeschlagen',
                    },
                },
                mailing: {
                    messageTypeOptions: {
                        email: 'E-Mail',
                        letter: 'Brief',
                    },
                    groupName: 'Vorlage',
                    name: 'Name',
                    description: 'Beschreibung',
                    status: 'Status',
                    type: 'Typ',
                    email: {
                        subject: 'E-Mail-Betreff',
                        previewText: 'Posteingang-Textvorschau',
                        tracking: 'E-Mail-Öffnungen verfolgen',
                        unsubscribe: 'Abmeldungen erlauben',
                        replyTo: 'Antwortadresse',
                    },
                    letter: {
                        sendColorPostal: 'Farbig per Post senden',
                        sendWithCheaperMethod: 'Mit günstigerer Methode senden',
                    },
                    onlySendToMissingEmailAddresses: 'Nur an Empfänger ohne E-Mail-Adresse senden',
                    scope: 'Bereich',
                    draft: 'Entwurf',
                    contactType: 'Kontakttyp',
                    tags: 'Mailing-Tags',
                    scheduledAt: 'Geplant für',
                    mailingStatusValues: {
                        draft: 'Entwurf',
                        send: 'Senden',
                        sending: 'Wird gesendet',
                        delivered: 'Zugestellt',
                    },
                    total: 'Empfänger gesamt',
                    costs: 'Kosten',
                    none: 'Keine',
                    manual: 'Manuell',
                    dispatching: 'Wird versendet',
                    dispatched: 'Versendet',
                    sent: 'Gesendet',
                    opened: 'Geöffnet',
                    failed: 'Fehlgeschlagen',
                    clicks: 'Klicks',
                    unsubscribes: 'Abmeldungen',
                },
                messageTemplate: {
                    groupName: 'Vorlage',
                    name: 'Name',
                    description: 'Beschreibung',
                    type: 'Typ',
                    email: {
                        subject: 'E-Mail-Betreff',
                        previewText: 'Posteingang-Textvorschau',
                    },
                    letter: {
                        sendColorPostal: 'Farbig per Post senden',
                        sendWithCheaperMethod: 'Mit günstigerer Methode senden',
                    },
                    scope: 'Bereich',
                    draft: 'Entwurf',
                    contactType: 'Kontakttyp',
                    allowedPlaces: 'Verfügbar für',
                    allowedPlacesOptions: {
                        mailings: 'Rundschreiben',
                        messages: 'Nachrichten',
                        automations: 'Automatisierungen',
                    },
                    scopeOptions: {
                        local: 'Lokal',
                        global: 'Global',
                    },
                },
                rule: {
                    groupName: 'Automatisierung',
                    contactType: 'Kontakttyp',
                    contactSegments: 'Kontaktsegmente',
                    trigger: 'Auslöser',
                    actions: 'Aktionen',
                    action: 'Aktion',
                    executionsCount: 'Ausführungen',
                    lastExecutedAt: 'Zuletzt ausgeführt am',
                    triggerType: {
                        created: 'Erstellt',
                        updated: 'Aktualisiert',
                        birthday: 'Geburtstag',
                        time: 'Wiederkehrender Zeitpunkt',
                        'member:entryAt': 'Mitgliedseintritt',
                        'member:exitAt': 'Mitgliedsaustritt',
                        'member:terminatedAt': 'Gekündigt am',
                        'member:membershipDuration': 'Mitgliedschaftsdauer',
                        'departments:entryAt': 'Abteilungseintritt',
                        'departments:exitAt': 'Abteilungsende',
                    },
                    actionType: {
                        sendContactEmail: 'Kontakt-E-Mail senden',
                        sendEmail: 'E-Mail an Adressen senden',
                    },
                    scheduleLabel: 'Zeitplan',
                    schedule: {
                        none: 'Kein Zeitplan',
                        shiftMode: {
                            before: 'vor',
                            after: 'nach',
                        },
                        shiftTime: 'Verschiebung',
                        time: 'Uhrzeit',
                        timeValue: 'Um {{time}}',
                    },
                    createRuleCompositionProperties: {
                        affectedProperties: {
                            affectedDepartment: {
                                groupName: 'Betroffene Abteilung',
                                name: 'Name der betroffenen Abteilung',
                                entryAt: 'Betroffene Abteilung Eintritt',
                                exitAt: 'Betroffene Abteilung Austritt',
                            },
                        },
                    },
                },
                contactRuleExecution: {
                    statusValues: {
                        pending: 'Ausstehend',
                        executing: 'Wird ausgeführt',
                        executed: 'Ausgeführt',
                    },
                    actionsResult: {
                        statusValues: {
                            success: 'Erfolgreich',
                            failed: 'Fehlgeschlagen',
                            unapplicable: 'Nicht anwendbar',
                        },
                    },
                },
                voucher: {
                    common: {
                        groupName: 'Allgemein',
                        name: 'Name',
                        code: 'Code',
                        amount: 'Betrag',
                        interval: 'Intervall',
                        startAt: 'Start',
                        endAt: 'Ende',
                    },
                    contact: {
                        groupName: 'Kontakt',
                        name: 'Name',
                    },
                    contract: {
                        groupName: 'Vertrag',
                        name: 'Name',
                    },
                    interval: {
                        month: 'Monatlich',
                        quarter: 'Quartalsweise',
                        halfYear: 'Halbjährlich',
                        year: 'Jährlich',
                        oneTime: 'Einmalig',
                    },
                },
            },
            sales: {
                opportunity: {
                    groupName: 'Verkaufschance',
                    name: 'Name',
                    stage: 'Phase',
                    contact: 'Kontakt',
                    expectedValue: 'Erwarteter Umsatz',
                    salesChance: 'Verkaufswahrscheinlichkeit (%)',
                    expectedCloseAt: 'Geplanter Abschluss',
                    responsibleUser: 'Verantwortlich',
                    products: 'Produkte',
                    product: 'Produkt',
                    quantity: 'Menge',
                    offerReceipt: 'Angebot',
                    renewalOf: 'Verlängerung von',
                    notes: 'Notizen',
                    lostReason: 'Verlustgrund',
                    createdAt: 'Erstellt am',
                    updatedAt: 'Aktualisiert am',
                    stages: {
                        new: 'Neu',
                        qualified: 'Qualifiziert',
                        offer: 'Angebot',
                        negotiation: 'Verhandlung',
                        won: 'Gewonnen',
                        lost: 'Verloren',
                    },
                },
            },
            booking: {
                bookingType: {
                    event: 'Event',
                    resource: 'Ressource',
                },
                bookingViewType: {
                    event: 'Events',
                    resource: 'Ressourcen',
                },
                bookingViewStyle: {
                    group: 'Gruppe',
                    list: 'Liste',
                    schedule: 'Zeitplan',
                    plan: 'Plan',
                    sites: 'Standorte',
                },
                bookingCategory: {
                    name: 'Name',
                    description: 'Beschreibung',
                    availability: 'Verfügbar für',
                    availabilityValues: {
                        all: 'Alle',
                        events: 'Events',
                        resources: 'Ressourcen',
                    },
                },
                bookingStatusValue: {
                    confirmed: 'Bestätigt',
                    partialCancelation: 'Stornierung(en)',
                    canceled: 'Storniert',
                },
                bookingFormSettingsAddressMode: {
                    none: 'Keine Adresse',
                    optional: 'Optionale Adresse',
                    required: 'Adresse wird benötigt',
                },
                bookingFormSettingsApplicantType: {
                    person: 'Nur Personen',
                    organization: 'Nur Organisationen',
                    both: 'Personen und Organisationen',
                },
                booking: {
                    booking: {
                        groupName: 'Buchung',
                        createdAt: 'Gebucht am',
                        bookingNumber: 'Nummer',
                        type: 'Art',
                        status: 'Status',
                        records: {
                            event: 'Teilnehmer',
                            resource: 'Ressourcen',
                            any: 'Anzahl',
                        },
                    },
                    payment: {
                        groupName: 'Zahlung',
                        status: 'Status',
                        paymentMethod: 'Methode',
                        price: 'Preis',
                        points: 'Punkte',
                        usedPunchCardNumber: 'Punktekarten-Nr.',
                        discountCode: 'Rabattcode',
                    },
                    buyer: {
                        groupName: 'Käufer / Anmelder',
                        name: 'Name',
                        personName: 'Name der Person',
                        email: 'E-Mail',
                        phone: 'Telefon',
                        address: 'Adresse',
                        user: 'Benutzer',
                    },
                },
                offerMode: {
                    disabled: 'Deaktiviert',
                    private: 'Privat',
                    protected: 'Geschützt',
                    public: 'Öffentlich',
                },
                event: {
                    event: {
                        event: {
                            groupName: 'Event',
                            title: 'Titel',
                            description: 'Beschreibung',
                            tags: 'Tags',
                            categories: 'Kategorien',
                        },
                        schedule: {
                            groupName: 'Zeitraum',
                            from: 'Beginnt am',
                            to: 'Endet am',
                            timezone: 'Zeitzone',
                            recurring: 'Wiederkehrend',
                            status: 'Verfügbarkeitsstatus',
                        },
                        place: {
                            groupName: 'Ort',
                            type: 'Art',
                            site: 'Standort',
                            resource: 'Ressource',
                            address: 'Adresse',
                            url: 'Online-URL',
                        },
                        booking: {
                            groupName: 'Buchung',
                            mode: 'Angebotsmodus',
                            offer: 'Angebot',
                            attendeeMode: 'Teilnehmermodus',
                            bookingRestriction: 'Buchungsbeschränkung',
                            availableFrom: 'Buchbar ab',
                            availableTo: 'Buchbar bis',
                            maxAttendees: 'Maximale Teilnehmer',
                            permanent: 'Dauerbuchung',
                            waitlist: 'Warteliste',
                        },
                        instructors: {
                            groupName: 'Kursleiter',
                            name: 'Kursleiter',
                            instructor: 'Kursleiter',
                        },
                        statistics: {
                            groupName: 'Statistik',
                            attendees: 'Teilnehmer',
                            teamMembers: 'Teammitglieder',
                            waitlisted: 'Auf der Warteliste',
                            offered: 'Angeboten',
                            pending: 'Ausstehend',
                            confirmed: 'Bestätigt',
                            canceled: 'Storniert',
                            checkedIn: 'Eingecheckt',
                        },
                        status: {
                            groupName: 'Status',
                            canceledAt: 'Storniert am',
                        },
                        placeType: {
                            resource: 'Ressource',
                            address: 'Adresse',
                            online: 'Online',
                        },
                        periodStatus: {
                            available: 'Verfügbar',
                            blockedResource: 'Durch Ressource blockiert',
                            blockedCalendar: 'Durch Kalender blockiert',
                        },
                        attendeeMode: {
                            multiple: 'Mehrere Personen',
                            single: 'Einzelperson',
                            team: 'Team',
                            rate: 'Tarif',
                        },
                        bookingRestriction: {
                            soldOut: 'Ausverkauft',
                            bookIntoWaitlist: 'Wartelistenbuchung',
                            requireConfirmation: 'Bestätigung erforderlich',
                        },
                    },
                    attendeeParticipantType: {
                        person: 'Person',
                        team: 'Team',
                    },
                    attendee: {
                        common: {
                            name: 'Name',
                            personName: 'Name der Person',
                            membershipNumber: 'Mitglied',
                            user: 'Benutzer',
                            address: 'Adresse',
                            email: 'E-Mail',
                            phone: 'Telefon',
                        },
                        booking: {
                            groupName: 'Buchung',
                            recordNumber: 'Nummer',
                            rate: 'Tarif',
                            rateVariant: 'Variante',
                            serviceName: 'Services',
                        },
                        attendee: {
                            groupName: 'Teilnehmer',
                        },
                        team: {
                            groupName: 'Team',
                        },
                        applicant: {
                            groupName: 'Anmelder',
                        },
                        waitlist: {
                            groupName: 'Warteliste',
                            listNumber: 'Wartelistennummer',
                            offerStatus: 'Angebotsstatus',
                        },
                        status: {
                            groupName: 'Status',
                            status: 'Status',
                            createdAt: 'Hinzugefügt am',
                            checkedInAt: 'Eingecheckt am',
                            canceledAt: 'Storniert am',
                        },
                        statusValue: {
                            attendees: 'Teilnehmer',
                            teams: 'Teams',
                            teamMembers: 'Teammitglieder',
                            waitlisted: 'Warteliste',
                            offered: 'Angeboten',
                            pending: 'Ausstehend',
                            confirmed: 'Bestätigt',
                            canceled: 'Storniert',
                            checkedIn: 'Eingecheckt',
                        },
                        waitlistOfferStatusValue: {
                            manual: 'Manuell',
                            pending: 'Ausstehend',
                            accepted: 'Akzeptiert',
                            rejected: 'Abgelehnt',
                        },
                        tickets: {
                            groupName: 'Daten des Tickets',
                        },
                        form: {
                            groups: {
                                tickets: 'Tickets',
                            },
                            fields: {
                                attendeeName: 'Teilnehmer Name',
                            },
                        },
                    },
                    eventOffer: {
                        team: {
                            labelDefault: 'Person(en)',
                        },
                    },
                },
                resource: {
                    resource: {
                        resource: {
                            groupName: 'Ressource',
                            name: 'Name',
                            type: 'Art',
                            description: 'Beschreibung',
                            tags: 'Tags',
                            categories: 'Kategorien',
                            attachable: 'Anhängbar',
                            shared: 'Geteilt',
                        },
                        sites: {
                            groupName: 'Standorte',
                            name: 'Standorte',
                            site: 'Standort',
                            address: 'Adresse',
                            timezone: 'Zeitzone',
                            stock: 'Bestand',
                        },
                        booking: {
                            groupName: 'Buchung',
                            mode: 'Angebotsmodus',
                            offer: 'Angebot',
                            slotSize: 'Größe der Buchungszeit',
                            slotUnit: 'Einheit der Buchungszeit',
                            minimumSlots: 'Minimale Buchungszeiten',
                            maximumSlots: 'Maximale Buchungszeiten',
                            availableOnWatchlist: 'Auf Merkliste verfügbar',
                            checkIn: 'Check-in verfügbar',
                        },
                        slotUnit: {
                            minutes: 'Minuten',
                            hours: 'Stunden',
                            days: 'Tage',
                        },
                    },
                    resourceType: {
                        place: {
                            single: 'Ort',
                            multi: 'Orte',
                        },
                        person: {
                            single: 'Person',
                            multi: 'Personen',
                        },
                        vehicle: {
                            single: 'Fahrzeug',
                            multi: 'Fahrzeuge',
                        },
                        tool: {
                            single: 'Werkzeug',
                            multi: 'Werkzeuge',
                        },
                        clothing: {
                            single: 'Kleidung',
                            multi: 'Kleidungsstücke',
                        },
                        object: {
                            single: 'Objekt',
                            multi: 'Objekte',
                        },
                        other: {
                            single: 'Sonstiges',
                            multi: 'Sonstige',
                        },
                    },
                    reservation: {
                        reservationType: {
                            booking: 'Buchung',
                            event: 'Event',
                            custom: 'Benutzerdefiniert',
                        },
                        statusValue: {
                            pending: 'Ausstehend',
                            confirmed: 'Bestätigt',
                            canceled: 'Storniert',
                            checkedIn: 'Eingecheckt',
                            // --
                            reserved: 'Reserviert',
                            maintenance: 'Wartung',
                            blocked: 'Gesperrt',
                            outOfOrder: 'Außer Betrieb',
                            closed: 'Geschlossen',
                            other: 'Sonstiges',
                        },
                        reservation: {
                            groupName: 'Reservierung',
                            type: 'Art',
                            periodStart: 'Von',
                            periodEnd: 'Bis',
                            site: 'Site',
                            quantity: 'Menge',
                            resource: 'Resource',
                            description: 'Beschreibung',
                        },
                        booking: {
                            groupName: 'Buchung',
                            applicant: {
                                groupName: 'Anmelder',
                                name: 'Name',
                                membershipNumber: 'Mitglied',
                                user: 'Benutzer',
                                email: 'E-Mail',
                                phone: 'Telefon',
                                address: 'Adresse',
                            },
                            recordNumber: 'Buchung',
                            rate: 'Tarif',
                            rateVariant: 'Variante',
                            serviceName: 'Services',
                        },
                        status: {
                            groupName: 'Status',
                            status: 'Status',
                            createdAt: 'Erstellt am',
                            checkedInAt: 'Eingecheckt am',
                            canceledAt: 'Storniert am',
                        },
                        event: {
                            groupName: 'Event',
                            event: 'Event',
                        },
                    },
                    checkIn: {
                        checkIn: {
                            groupName: 'Check-In',
                            contact: 'Kontakt',
                            checkInAt: 'Eingecheckt am',
                            checkOutAt: 'Ausgecheckt am',
                            key: 'Schlüssel',
                            options: 'Optionen',
                            site: 'Standort',
                            resource: 'Ressource',
                        },
                    },
                },
                issuedPunchCard: {
                    status: {
                        active: 'Aktiv',
                        expired: 'Abgelaufen',
                        locked: 'Gesperrt',
                        canceled: 'Storniert',
                    },
                },
            },
            finance: {
                account: {
                    debtor: {
                        blocked: 'Debitor wurde gesperrt am {{blockedAt}}',
                        debtor: {
                            debtor: {
                                groupName: 'Debitor',
                                account: 'Konto',
                                name: 'Name',
                                details: 'Details',
                                contact: 'Kontakt',
                            },
                            status: {
                                groupName: 'Status',
                                blockedAt: 'Gesperrt am',
                                blockMode: 'Sperrmodus',
                                blockModes: {
                                    all: 'Vollständige Sperrung',
                                    directDebit: 'Zahlungen einschränken',
                                },
                                blockedNote: 'Sperrnotiz',
                                disableAutomaticReminders: 'Automatische Mahnungen deaktiviert',
                                totalOwedFromDebtor: 'Offen',
                                totalOwedFromDebtorInfo:
                                    'Der gesamte offene Betrag inklusive aller Gebühren die der Debitor noch zahlen muss',
                                totalOwedToDebtor: 'Geschuldet',
                                totalOwedToDebtorInfo:
                                    'Der gesamte Betrag den du dem Debitor schuldest z.B. durch Gutschriften',
                                totalDeposit: 'Guthaben',
                                totalDepositInfo:
                                    'Das Gesamtguthaben des Debitors von Anzahlungen das mit Rechnungen verrechnet wird',
                            },
                            payment: {
                                groupName: 'Zahlung',
                                paymentMethodType: 'Zahlungsmethode',
                                sepaDirectDebitMandate: {
                                    iban: 'IBAN',
                                    bic: 'BIC',
                                    sepaMandateId: 'Mandatsreferenz',
                                    sepaMandateSignatureDate: 'Signaturdatum',
                                },
                            },
                            other: {
                                groupName: 'Anderes',
                                receiptSendMethod: 'Belegversand',
                                supplierNumber: 'Lieferantennummer',
                                email: 'E-Mail',
                                address: 'Adresse',
                            },
                        },
                    },
                    creditor: {
                        creditor: {
                            creditor: {
                                groupName: 'Kreditor',
                                account: 'Konto',
                                name: 'Name',
                                details: 'Details',
                            },
                            status: {
                                groupName: 'Status',
                                totalOwedToCreditor: 'Offen',
                                totalOwedToCreditorInfo:
                                    'Der gesamte offene Betrag inklusive aller Gebühren die du dem Kreditor noch auszahlen muss',
                                totalOwedFromCreditor: 'Geschuldet',
                                totalOwedFromCreditorInfo:
                                    'Der gesamte offene Betrag die der Kreditor dir schuldet z.B. durch Gutschriften',
                            },
                            payment: {
                                groupName: 'Zahlung',
                                paymentMethodType: 'Zahlungsmethode',
                                paymentMethodTypeValue: {
                                    cash: 'Bar',
                                    creditTransfer: 'Überweisung',
                                },
                                creditTransfer: {
                                    iban: 'IBAN',
                                    bic: 'BIC',
                                    accountHolderName: 'Kontoinhaber',
                                },
                            },
                            other: {
                                groupName: 'Sonstiges',
                                supplierNumber: 'Lieferantennummer',
                            },
                        },
                    },
                },
                accounting: {
                    account: {
                        number: 'Kontonummer',
                        label: 'Bezeichnung',
                        taxCode: 'Steuerschlüssel',
                        bookable: 'Buchbar',
                        datevAutomatic: 'DATEV-Automatik',
                        group: {
                            capitalAsset: 'Anlagevermögen',
                            liquidAsset: 'Liquidität',
                            cash: 'Bank- und Kassenbestand',
                            equityCapital: 'Eigenkapital',
                            debtCapital: 'Fremdkapital',
                            revenue: 'Einnahmen',
                            expense: 'Ausgaben',
                            static: 'Statistisch neutral',
                            staticDebit: 'Statistisch Soll-Saldo',
                            staticCredit: 'Statistisch Haben-Saldo',
                            debtor: 'Debitoren',
                            creditor: 'Kreditoren',
                        },
                    },
                    accountingPlanType: {
                        SKR42EUR: 'SKR 42 für Einnahmen-Überschuss-Rechnung',
                        SKR42GUV: 'SKR 42 für GuV/Bilanzierer',
                        SKR03GUV: 'SKR 03 für GuV/Bilanzierer',
                        SKR04GUV: 'SKR 04 für GuV/Bilanzierer',
                        SKR07EAR: 'SKR 07 für Einnahmen-Ausgaben-Rechnung (Österreich)',
                        SKR07GUV: 'SKR 07 für GuV/Bilanzierer (Österreich)',
                    },
                    posting: {
                        postingSource: {
                            manual: 'Einzelbuchung',
                            expense: 'Ausgabe',
                            revenue: 'Einnahme',
                            invoice: 'Rechnung',
                            deposit: 'Anzahlung',
                            donation: 'Spende',
                            offer: 'Angebot',
                            confirmation: 'Auftragsbestätigung',
                            cashRegister: 'Kasse',
                            cashTransaction: 'Banktransaktion',
                            datevImport: 'Datev import',
                            forward: 'Eröffnung',
                            postingReceipt: 'Sammelbuchung',
                            fixedAsset: 'Anlagevermögen',
                            donationPosition: 'Spendeneingang',
                        },
                        posting: {
                            groupName: 'Einzelbuchung',
                            number: 'Nummer',
                            receiptDate: 'Datum',
                            receiptNumber: 'Belegnummer',
                            text: 'Beschreibung',
                            amount: 'Betrag',
                            taxCode: 'Steuer',
                            taxAmount: 'Steuerbetrag',
                            debitAccount: 'Soll',
                            creditAccount: 'Haben',
                            costCenter1: 'Kst. 1',
                            costCenter2: 'Kst. 2',
                        },
                        other: {
                            groupName: 'Anderes',
                            reverse: 'Generalumkehr',
                            committedAt: 'Festgeschrieben am',
                            source: 'Quelle',
                            deliveryDate: 'Lieferdatum',
                            receiptFile: 'Belegdatei',
                            taxDebitAccount: 'Steuer Soll',
                            taxCreditAccount: 'Steuer Haben',
                            isGross: 'Brutto-Buchung',
                        },
                    },
                },
                cash: {
                    onlineDebits: {
                        dispatchDue: {
                            developmentOnly:
                                'Online-Einzüge können nur in der Entwicklungsumgebung zur Verarbeitung eingeplant werden',
                            notFound: 'Online-Einzug wurde nicht gefunden',
                        },
                        reasons: {
                            default: {
                                customerActionRequired:
                                    'Der Zahlungsanbieter erfordert, dass der Kunde die Zahlungsmethode aktualisiert oder bestätigt.',
                                authenticationRequired:
                                    'Die Bank erfordert, dass der Kunde diese Zahlungsmethode bestätigt.',
                                paymentMethodUnavailable: 'Die Zahlungsmethode ist nicht mehr verfügbar.',
                                paymentAuthorizationInactive: 'Die Zahlungsautorisierung ist nicht mehr aktiv.',
                                paymentMethodExpired: 'Die gespeicherte Zahlungsmethode ist abgelaufen.',
                            },
                            paypal: {
                                payerActionRequired:
                                    'PayPal erfordert, dass der Kunde die Zahlungsmethode genehmigt oder aktualisiert.',
                                payerCannotPay: 'Das PayPal-Konto kann aktuell nicht belastet werden.',
                                vaultTokenDeleted: 'Der Online-Einzug ist nicht mehr aktiv.',
                            },
                            stripe: {
                                setupFailed:
                                    'Der Zahlungsanbieter erfordert, dass der Kunde die Zahlungsmethode aktualisiert oder bestätigt.',
                                cardDeclined: 'Die Karte wurde abgelehnt.',
                            },
                        },
                    },
                    cashAccount: {
                        source: {
                            bank: 'Bankkonto',
                            paypal: 'Paypal-Konto',
                            stripe: 'Stripe-Konto',
                        },
                    },
                    cashTransaction: {
                        date: 'Datum',
                        amount: 'Betrag',
                        purpose: 'Verwendungszweck',
                        counterpartName: 'Gegenseite Name',
                        counterpartAccount: 'Gegenseite Konto',
                    },
                    cashTransactionRule: {
                        matcher: {
                            type: 'Typ',
                            amount: 'Betrag',
                            purpose: 'Verwendungszweck',
                            counterpartName: 'Gegenseite Name',
                            counterpartAccount: 'Gegenseite Konto',
                        },
                        outcome: 'Ergebnis',
                    },
                },
                receipts: {
                    receipt: {
                        receipt: {
                            receipt: {
                                groupName: 'Beleg',
                                type: 'Art',
                                invoiceType: 'Rechnungsart',
                                receiptNumber: 'Nummer',
                                receiptDate: 'Belegdatum',
                                dueDate: 'Fälligkeitsdatum',
                                draft: 'Entwurf',
                                refund: 'Rückerstattung',
                                canceledBy: 'Storniert',
                            },
                            recipient: {
                                groupName: 'Empfänger',
                                accountName: 'Name',
                                account: 'Konto',
                                customerType: 'Art',
                                customerNumber: 'Nummer',
                                sendMethod: 'Versandart',
                                receiptDispatchStatusSendStatus: 'Versandstatus',
                            },
                            positions: {
                                groupName: 'Positionen',
                                description: 'Beschreibung',
                                quantity: 'Menge',
                                unit: 'Einheit',
                                unitAmount: 'Betrag',
                                taxCode: 'Steuer',
                                discount: 'Rabatt',
                                amount: 'Gesamt',
                                account: 'Konto',
                                costCenter1: 'Kst. 1',
                                costCenter2: 'Kst. 2',
                            },
                            reminderStatus: {
                                groupName: 'Mahnstatus',
                                level: 'Stufe',
                                fees: 'Gebühren',
                                reminders: 'Mahnungen',
                            },
                            reminders: {
                                groupName: 'Mahnungen',
                                level: 'Stufe',
                                date: 'Datum',
                                fee: 'Gebühren',
                                unpaidAmount: 'Unbezahlt',
                            },
                            payment: {
                                groupName: 'Zahlung',
                                isNet: 'Netto',
                                totalGrossAmount: 'Gesamt',
                                totalAmountLeftToPay: 'Zu zahlen',
                                totalFeesAmount: 'Gebühren',
                                paidAt: 'Bezahlt am',
                                paymentStatus: 'Zahlungsstatus',
                                paymentDifference: 'Differenz',
                                paymentMethod: 'Zahlungsmethode',
                            },
                            payments: {
                                groupName: 'Zahlungen',
                                date: 'Datum',
                                amount: 'Betrag',
                                account: 'Konto',
                                cashTransaction: 'Transaktion',
                            },
                            returnedPayments: {
                                groupName: 'Rückläufer',
                                date: 'Datum',
                                returnFee: 'Bankgebühren',
                                processingFee: 'Bearbeitungsgebühren',
                                action: 'Behandlung',
                                unpaidAmount: 'Unbezahlt',
                            },
                            cancelation: {
                                groupName: 'Stornierung',
                                canceledAt: 'Datum',
                                canceledReason: 'Grund',
                                cancelReceiptNumber: 'Beleg-Nummer',
                            },
                            other: {
                                groupName: 'Anderes',
                                refundReceiptNumber: 'Gutschrift-Nummer',
                                tags: 'Tags',
                                archived: 'Archiviert',
                                contractCollection: 'Vertragsabrechnung',
                            },
                        },
                        returnedPaymentAction: {
                            retry: 'Erneut im Einzug',
                            manual: 'Manuelle Bezahlung',
                            cancel: 'Beleg storniert',
                        },
                        type: {
                            expense: 'Ausgabe',
                            expenses: 'Ausgaben',
                            expenseInfo: 'Beleg für eine Ausgabe, die du getätigt hast',
                            expenseRefund: 'Ausgabenrückerstattung',
                            expensesRefund: 'Ausgabenerstattungen',
                            expenseRefundInfo: 'Beleg, wenn du eine Rückerstattung für eine Ausgabe erhalten hast',
                            revenue: 'Einnahme',
                            revenues: 'Einnahmen',
                            revenueInfo: 'Einfacher Beleg für eine Einnahme, die du bereits getätigt hast',
                            revenueRefund: 'Einnahmenrückerstattung',
                            revenuesRefund: 'Einnahmenerstattungen',
                            revenueRefundInfo: 'Ein Beleg für eine rückerstattete Einnahme',
                            offer: 'Angebot',
                            offers: 'Angebote',
                            offerInfo: 'Ein Angebot für einen Verkauf',
                            confirmation: 'Auftragsbestätigung',
                            confirmations: 'Auftragsbestätigungen',
                            confirmationInfo: 'Eine Bestätigung, wenn jemand dein Angebot angenommen hat',
                            donation: 'Spende',
                            donations: 'Spenden',
                            donationInfo: 'Eine Spende, die du erhalten hast oder erhalten wirst',
                            invoice: 'Rechnung',
                            invoices: 'Rechnungen',
                            invoiceInfo: 'Eine reguläre Rechnung für Verkäufe',
                            invoiceRefund: 'Gutschrift',
                            invoicesRefund: 'Gutschriften',
                            invoiceRefundInfo: 'Erstattung einer bereits bezahlten Rechnung, die du erstellt hast',
                            deposit: 'Anzahlung',
                            deposits: 'Anzahlungen',
                            depositInfo: 'Ein Beleg für eine erhaltene Vorauszahlung',
                            depositRefund: 'Gutschrift',
                            depositsRefund: 'Gutschriften',
                            depositRefundInfo: 'Erstattung einer bereits bezahlten Anzahlung',
                            // -- invoice types
                            invoiceMembershipFee: 'Beitragsrechnung',
                            invoiceMembershipFeeInfo: 'Eine Rechnung für Mitgliedsbeiträge',
                            invoiceBooking: 'Buchungsrechnung',
                            invoiceBookingInfo: 'Eine Rechnung für eine Buchung (Kurs, Ort etc.)',
                            // -- receipt template types
                            reminder: 'Mahnung',
                            returnedPayment: 'Rückläufer',
                            donationReminder: 'Spendenerinnerung',
                            // -- fake receipt types
                            invoiceCancelation: 'Stornorechnung',
                        },
                        typeMulti: {
                            offer: '{{count, number}} Angebote',
                            offerInfo: 'Angebote für Verkäufe',
                            confirmation: '{{count, number}} Auftragsbestätigungen',
                            confirmationInfo: 'Bestätigungen, wenn jemand dein Angebot angenommen hat',
                            donation: '{{count, number}} Spenden',
                            donationInfo: 'Spenden, die du erhalten hast oder erhalten wirst',
                            invoice: '{{count, number}} Rechnungen',
                            invoiceInfo: 'Reguläre Rechnungen für Verkäufe',
                            deposit: '{{count, number}} Anzahlungen',
                            depositInfo: 'Belege für erhaltene Vorauszahlungen',
                            // both below are never used but make TS happy
                            invoiceRefund: 'Gutschrift',
                            invoiceRefundInfo: 'Erstattung einer bereits bezahlten Rechnung',
                        },
                        customerType: {
                            customer: 'Kundennummer',
                            member: 'Mitgliedsnummer',
                            debtor: 'Debitornummer',
                            booking: 'Buchungsnummer',
                        },
                        deliveryDateType: {
                            none: 'Kein Lieferdatum',
                            delivery: 'Lieferdatum',
                            deliveryPeriod: 'Lieferzeitraum',
                            service: 'Servicedatum',
                            servicePeriod: 'Servicezeitraum',
                        },
                        sampleTexts: {
                            accountName: 'Max Mustermann',
                            details1: 'z.Hd. Frau Mustermann',
                            details2: 'Wohnung 4',
                            addressLine: 'Musterstraße 123',
                            city: 'Musterstadt',
                            zip: '12345',
                            intro: 'Optionaler Einleitungstext für den Beleg',
                            note: 'Optionale Notiz für den Beleg',
                            deliveryTerms: 'Lieferbedingungen für den Beleg',
                            positionDescription: 'Position',
                            positionDetails: 'Extra Beschreibung',
                            positionUnit: 'Einheit',
                            bankName: 'Beispiel-Bank',
                            // -- reminder
                            reminder: {
                                title: 'Zahlungserinnerung',
                                intro: 'Sehr geehrte Damen und Herren,\n\nin der Hektik des Alltags kann man leicht mal eine Rechnung übersehen. Deshalb schreiben wir Ihnen heute. Zu unten aufgeführten Posten ist bei uns noch keine Zahlung eingegangen.',
                                note: 'Vielleicht haben Sie die Rechnung schon bezahlt und der Betrag ist nur noch nicht auf unserem Konto verbucht. Dann ist alles in Ordnung und Sie können diesen Brief einfach wegwerfen. Sollten Sie die Rechnung tatsächlich vergessen haben, bitten wir Sie, den Betrag nun zeitnah zu überweisen. Vielen Dank!',
                            },
                            // returnedPayment
                            returnedPayment: {
                                title: 'Rückläufer',
                                intro: 'Sehr geehrte Damen und Herren,\n\nLeider ging die Zahlung für die folgende Rechnung zurück. Wir bitten Sie, die Rechnung umgehend inklusive aller Gebühren zu begleichen.',
                                note: 'Sollte es Probleme mit der Zahlung oder Zahlungsmethode geben, treten Sie bitte mit uns zeitnah in Kontakt.',
                            },
                        },
                    },
                    receiptTextBlockType: {
                        multiple: {
                            title: 'Belegtitel',
                            intro: 'Einleitungstexte',
                            deliveryTerms: 'Lieferbedingungen',
                            note: 'Fußnoten',
                        },
                        single: {
                            title: 'Belegtitel',
                            intro: 'Einleitungstext',
                            deliveryTerms: 'Lieferbedingungen',
                            note: 'Fußnoten',
                        },
                    },
                },
                reminderLevel: {
                    level1: 'Erinnerung',
                    level2: 'Erste Mahnung',
                    level3: 'Zweite Mahnung',
                    level4: 'Dritte Mahnung',
                },
                settings: {
                    vatDueMethod: {
                        receipt: 'Soll-Versteuerung (Fällig bei Rechnung)',
                        payment: 'Ist-Versteuerung (Fällig bei Zahlung)',
                    },
                },
                paymentMethodType: {
                    sepaCreditTransfer: 'Überweisung',
                    sepaDirectDebit: 'Lastschrift',
                    cash: 'Bar',
                    online: 'Online',
                },
                paymentStatus: {
                    unpaid: 'Unbezahlt',
                    partial: 'Teilbezahlt',
                    paid: 'Bezahlt',
                },
                paymentDifference: {
                    up_unpaid: 'Teilweise bezahlt',
                    up_discountReduction: 'Rabatt / Nachlass',
                    up_feeNoTax: 'Bankgebühren ohne Steuern',
                    up_feeTax: 'Bankgebühren mit Steuern',
                    op_feeNoTax: 'Bankgebühren ohne Steuern',
                    op_feeTax: 'Bankgebühren mit Steuern',
                    op_interestFee: 'Mahngebühren',
                    op_donation: 'Als Spende erfassen',
                    op_open: 'Differenz offen lassen',
                },
                paymentMethodsSettings: {
                    sepaCreditTransfer: {
                        paymentTerms:
                            'Bitte überweise den fälligen Betrag von **[[totalDueAmount]]** bis zum **[[dueDate]]** mit dem Verwendungszweck **[[sepaCreditTranfer.referenceId]]** auf unser Konto bei der [[sepaCreditTranfer.bankName]]:\n\nIBAN: **[[sepaCreditTranfer.iban]]**        BIC: [[sepaCreditTranfer.bic]]\n\nOder scanne den Girocode auf der rechten Seite mit deiner Banking-App.',
                    },
                    sepaDirectDebit: {
                        paymentTerms:
                            'Wir ziehen den fälligen Betrag von **[[totalDueAmount]]** mit der SEPA-Lastschrift zum Mandat **[[sepaDirectDebit.mandateId]]** zu der Gläubiger-Identifikationsnummer [[sepaDirectDebit.creditorIdentifier]] von Ihrem Konto **[[sepaDirectDebit.iban]]** zum **[[dueDate]]** ein. Wir bitten Sie, für Kontodeckung zu sorgen.',
                    },
                    cash: {
                        paymentTerms:
                            'Der fällige Betrag von **[[totalDueAmount]]** wird bis zum **[[dueDate]]** in Bar beglichen',
                    },
                    online: {
                        paymentTerms:
                            'Der Betrag von **[[totalDueAmount]]** muss bis spätestens **[[dueDate]]** online bezahlt werden.',
                        manualPaymentTerms:
                            'Bitte bezahle den Betrag von **[[totalDueAmount]]** bis spätestens **[[dueDate]]** online.',
                    },
                },
            },
            channels: {
                channel: {
                    name: 'Name',
                    description: 'Beschreibung',
                },
                systemMessages: {
                    userRemoved: '{{initiatorUser}} entfernte {{affectedUsers}}',
                    userAdded: '{{initiatorUser}} fügte {{affectedUsers}}',
                    userLeft: '{{affectedUsers}} hat die Gruppe verlassen',
                    imageChanged: '{{initiatorUser}} hat das Profilbild geändert',
                    nameChanged: '{{initiatorUser}} hat den Namen zu "{{channelName}}" geändert',
                    descriptionChanged: '{{initiatorUser}} hat die Beschreibung geändert',
                    channelCreated: '{{initiatorUser}} hat "{{channelName}}" erstellt',
                    adminPromoted: '{{initiatorUser}} hat {{affectedUsers}} zu Administratoren ernannt',
                    adminDemoted: '{{initiatorUser}} hat {{affectedUsers}} die Administratorrechte entzogen',
                    joinedWithPublicLink: '{{initiatorUser}} ist über einen öffentlichen Link beigetreten',
                },
            },
            extension: {
                visibility: {
                    private: 'Privat',
                    public: 'Öffentlich',
                },
                category: {
                    all: 'Alle Kategorien',
                    smartAccess: 'Smart Access',
                    member: 'Mitgliedersoftware',
                    finances: 'Finanzsoftware',
                    pos: 'POS-Zahlungssysteme',
                    other: 'Andere',
                },
            },
            mandate: {
                allMandates: 'Alle Mandanten',
            },
            role: {
                name: 'Name',
                description: 'Beschreibung',
            },
        },
        oldEntities: {
            contactSegment: {
                defaultSegments: {
                    members: {
                        name: 'Aktuelle Mitglieder',
                        hint: 'Alle Mitglieder, die bereits eingetreten sind und noch nicht ausgetreten sind',
                    },
                    futureMembers: {
                        name: 'Zukünftige Mitglieder',
                        hint: 'Alle Mitglieder, die in Zukunft eintreten werden',
                    },
                    resignedMembers: {
                        name: 'Ausgetretene Mitglieder',
                        hint: 'Alle Mitglieder, die bereits ausgetreten sind',
                    },
                    allMembers: {
                        name: 'Alle Mitglieder',
                        hint: 'Alle Mitglieder unabhängig von Eintritts- oder Austrittsdatum',
                    },
                    allContacts: {
                        name: 'Alle Kontakte',
                        hint: 'Alle Kontakte ohne Filter',
                    },
                },
            },
            members: {
                contact: {
                    billing: {
                        billingMethod: {
                            sepaCreditTransfer: 'Überweisung',
                            sepaDirectDebit: 'Lastschrift',
                            cashPayment: 'Bar',
                            onlinePayment: 'Online',
                            swissQRBill: 'Schweizer QR-Rechnung',
                        },
                    },
                    personal: {
                        type: {
                            malePerson: 'Männlich',
                            femalePerson: 'Weiblich',
                            otherPerson: 'Divers',
                            business: 'Firma',
                            club: 'Verein',
                            association: 'Verband',
                            otherOrganisation: 'Organisation',
                            none: 'Nicht gesetzt',
                        },
                        greetings: {
                            organisation: 'Sehr geehrte Damen und Herren',
                            personalMale: 'Sehr geehrter Herr {{personLastName}}',
                            personalFemale: 'Sehr geehrte Frau {{personLastName}}',
                            personalOther: 'Hallo {{personFirstName}}',
                        },
                    },
                },
            },
        },
        donation: {
            terms: {
                confirmation: 'Spendenbestätigung',
                confirmation_germanReceipt: 'Zuwendungsbestätigung',
                confirmations: 'Spendenbestätigungen',
                confirmations_germanReceipt: 'Zuwendungsbestätigungen',
            },
            createDonationFormSchema: {
                groups: {
                    donation: 'Deine Spende',
                    donor: 'Deine Angaben',
                    payment: 'Zahlung',
                    summary: 'Zusammenfassung',
                },
                amount: 'Spendenbetrag',
                paymentMethod: 'Zahlungsmethode',
                summary: 'Zusammenfassung der Spende',
                firstName: 'Vorname',
                lastName: 'Nachname',
                organizationName: 'Name der Organisation',
                organizationNameOptional: 'Name der Organisation (optional)',
                organizationNameOptionalInfo: 'Fülle das aus, wenn du im Namen einer Organisation spendest',
                email: 'E-Mail-Adresse',
                phone: 'Telefon',
                address: 'Adresse',
                addressOptional: 'Adresse (optional)',
                wantsConfirmation: '{{confirmation}}',
                wantsConfirmationConfirm: 'Ich möchte eine {{confirmation}} erhalten',
            },
            createDonationFormTextTags: {
                categories: {
                    donation: 'Spende',
                    donor: 'Spender',
                },
                donation: {
                    amount: 'Betrag',
                    frequency: 'Häufigkeit',
                    plan: 'Plan',
                    interval: 'Intervall',
                    campaign: 'Kampagne',
                    paymentMethod: 'Zahlungsmethode',
                    receiptNumber: 'Belegnummer',
                },
                donor: {
                    name: 'Name',
                    firstName: 'Vorname',
                    lastName: 'Nachname',
                    email: 'E-Mail-Adresse',
                },
            },
            donationConfirmationTextTags: {
                donatorName: {
                    name: 'Zuwender Name',
                    description: 'Der volle Name des Zuwenders (Vor- und Nachname oder Name der Organisation)',
                },
                donatorFirstName: {
                    name: 'Zuwender Vorname',
                    description: 'Der Vorname des Zuwenders, für Organisationen enthält der Name den ganzen Namen',
                },
                donatorLastName: {
                    name: 'Zuwender Nachname',
                    description: 'Der Nachname des Zuwenders, für Organisationen ist dieser leer',
                },
                donationNumber: {
                    name: 'Spendennummer',
                    description: 'Die Nummer der Spende',
                },
                donationAmount: {
                    name: 'Spendenbetrag',
                    description: 'Der Gesamtbetrag der Spenden im Dokument',
                },
                donationDate: {
                    name: 'Spendendatum',
                    description: 'Das Datum der Spende, bei mehreren Spenden das Datum der ersten',
                },
                donationPeriod: {
                    name: 'Spendenzeitraum',
                    description: 'Der Zeitraum von der ersten bis zur letzten Spende im Dokument',
                },
                donationYear: {
                    name: 'Spendenjahr',
                    description: 'Das Jahr der Spenden im Dokument',
                },
                donationList: {
                    name: 'Liste der Einzelspenden',
                    description: 'Alle Spenden im Dokument mit Datum, Betrag und Beschreibung als Liste',
                    item: '{{date}}: {{amount}}',
                    itemWithDescription: '{{date}}: {{amount}} ({{description}})',
                },
                confirmationDate: {
                    name: 'Ausstellungsdatum',
                    description: 'Das Datum, an dem die Bestätigung oder der Dankesbrief erstellt wird',
                },
            },
            donationConfirmationProperties: {
                properties: {
                    sender: {
                        name: 'Absender',
                        description: 'Vollständige Absenderdetails mit Name und Adresse',
                    },
                    receiver: {
                        name: 'Empfänger',
                        description: 'Vollständige Empfängerdetails (Zuwender) mit Name und Adresse',
                    },
                    donationNumber: {
                        name: 'Spendennummer',
                        description: 'Die Nummer der Spende',
                    },
                    content: {
                        name: 'Inhalt',
                        description: 'Der Text aus den Spendeneinstellungen',
                    },
                },
            },
        },
        organization: {
            contactInvitationEmail: {
                subject: 'Dein Zugriff auf {{organizationName}} in der Vereins-App',
                greeting: 'Hallo,',
                singleContent:
                    'Über die Schaltfläche unten kannst du den folgenden Kontaktdatensatz bei **{{organizationName}}** mit deinem Konto verknüpfen:\n\n{{contactNames}}\n\nAnschließend kannst du über die Vereins-App auf damit verbundene Informationen wie Buchungen, Rechnungen oder Dokumente zugreifen.',
                multipleContent:
                    'Mehrere Kontaktdatensätze bei **{{organizationName}}** verwenden diese E-Mail-Adresse:\n\n{{contactNames}}\n\nÜber die Schaltfläche unten kannst du alle mit deinem Konto verknüpfen. Anschließend kannst du über die Vereins-App auf die mit dir verbundenen Informationen zugreifen.',
                singleAction: 'Mit der Vereins-App zugreifen',
                multipleAction: 'Mit der Vereins-App zugreifen',
            },
            organizationPermissions: {
                title: 'Berechtigungen',
                missingPermissions_one: 'Fehlende Berechtigung: {{permissions}}',
                missingPermissions_other: 'Fehlende Berechtigungen: {{permissions}}',
                categories: {
                    general: 'Allgemein',
                    crm: 'Personen',
                    booking: 'Events / Verleih',
                    contracts: 'Beiträge',
                    sales: 'Verkauf',
                    work: 'Arbeit',
                    donation: 'Spenden',
                    finance: 'Finanzen',
                    accounting: 'Buchhaltung',
                    communication: 'Kommunikation',
                    misc: 'Mehr',
                    portal: 'Portal',
                    connect: 'Vereins-App',
                    legacy: 'Legacy',
                },
                scopes: {
                    organization: 'Organisationseinstellungen',
                    user: 'Benutzerverwaltung',
                    apiKey: 'API-Schlüssel',
                    tag: 'Tags',
                    template: 'Vorlagen',
                    team: 'Teams',
                    selfServiceChange: 'Datenänderungen',
                    dispatchDocument: 'Postausgang',
                    notification: 'Benachrichtigungen',
                    'contact:address': 'Adressen',
                    'contact:member': 'Mitglieder',
                    'contact:customer': 'Kunden',
                    'contact:sponsor': 'Sponsoren',
                    'contact:contributor': 'Ehrenamtliche',
                    'contact:employee': 'Mitarbeiter',
                    'contact:supplier': 'Lieferanten',
                    contactSegment: 'Ansichten',
                    department: 'Abteilungen',
                    contactHistory: 'Kontakthistorie',
                    contactNote: 'Kontaktnotizen',
                    contactDocument: 'Kontaktdokumente',
                    contactGroupType: 'Gruppentypen',
                    contactGroup: 'Gruppen',
                    contactGroupMembership: 'Gruppenmitgliedschaften',
                    conversation: 'Posteingang',
                    conversationFolder: 'Posteingangsordner',
                    applicationForm: 'Antragsformulare',
                    applicationSubmission: 'Einreichungen',
                    questionnaireForm: 'Formulare und Umfragen',
                    questionnaireSubmission: 'Formular- und Umfrageeinsendungen',
                    publishedApp: 'Apps',
                    proofType: 'Nachweisarten',
                    proof: 'Nachweise',
                    drive: 'Dateien',
                    certificateType: 'Zertifikatsarten',
                    certificate: 'Zertifikate',
                    contract: 'Beiträge',
                    contractCollection: 'Abrechnungen',
                    contactRule: 'Automatisierungen',
                    rule: 'Automatisierungen',
                    plan: 'Beitragsarten',
                    voucher: 'Gutscheine',
                    voucherTemplate: 'Gutscheinvorlagen',
                    contactPassTemplate: 'Pass-Vorlagen',
                    contactIssuedPass: 'Ausgegebene Pässe',
                    crmSettings: 'Kontakteinstellungen',
                    mailing: 'Mailings',
                    social: 'Social Media',
                    mandate: 'Mandanten',
                    accountingPlan: 'Kontenplan',
                    costCenter: 'Kostenstellen',
                    cashRegister: 'Kassen',
                    compensationClaim: 'Abrechnungen Aufwandsentschädigungen',
                    compensationClaimApproval: 'Freigabe Aufwandsentschädigungen',
                    workHours: 'Arbeitsstunden',
                    workHoursSettlement: 'Abrechnung Arbeitsstunden',
                    cashAccount: 'Bank- und Geldkonten',
                    cashTransaction: 'Kontobewegungen',
                    cashTransactionRule: 'Automatische Buchungsregeln',
                    sepaCreditTransfer: 'Überweisungen',
                    sepaDirectDebit: 'Lastschriften',
                    onlineDebitCollection: 'Online-Einzüge',
                    receipt: 'Rechnungen und Belege',
                    receiptTextBlock: 'Belegtextbausteine',
                    posting: 'Buchungsjournal',
                    financesSettings: 'Finanzeinstellungen',
                    debtor: 'Debitoren',
                    creditor: 'Kreditoren',
                    fixedAsset: 'Anlagenverwaltung',
                    product: 'Produkte',
                    inventory: 'Bestand',
                    salesPeriod: 'Verkaufszeiträume',
                    opportunity: 'Verkaufschancen',
                    todoItem: 'Aufgaben',
                    todoTopic: 'Aufgabenthemen',
                    todoProject: 'Aufgabenprojekte',
                    donationsSettings: 'Spendeneinstellungen',
                    donationForm: 'Spendenformulare',
                    donationFormSubmission: 'Einsendungen von Spendenformularen',
                    donation: 'Erhaltene Spenden',
                    austrianDonationSubmission: 'Spendenübermittlungen',
                    booking: 'Buchungen',
                    bookingDiscountCode: 'Rabattcodes',
                    bookingCategory: 'Kategorien',
                    bookingView: 'Webseitenansichten',
                    event: 'Events',
                    eventOffer: 'Angebotseinstellungen für Events',
                    attendee: 'Teilnehmer',
                    site: 'Standorte',
                    resource: 'Ressourcen',
                    resourceOffer: 'Angebotseinstellungen für Ressourcen',
                    resourceReservation: 'Reservierungen',
                    resourceCheckIn: 'Check-Ins',
                    supplementaryService: 'Zusatzleistungen',
                    punchCard: 'Punktekarten',
                    portalSettings: 'Portal-Einstellungen',
                    room: 'Räume',
                    // -----------
                    // OLD CAMPAI
                    // -----------
                    file: 'Dateien & Dokumente',
                    // contact
                    contact: 'Mitglieder & Kontakte',
                    contactReport: 'Auswertungen',
                    customFieldGroup: 'Eigene Felder',
                    contactTag: 'Mitglieder- & Kontakt-Tags',
                    contactMembershipSection: 'Abteilungen',
                    // messages
                    outgoingMessage: 'Nachrichtenausgang',
                    messageTemplate: 'Nachrichtenvorlagen',
                    // forms
                    form: 'Formulare / Anträge',
                    formSubmission: 'Antworten auf Formulare',
                    // contributions
                    contributionRate: 'Beitragsarten verwalten',
                    contribution: 'Beiträge & Spenden abrechnen',
                    billing: 'Finanz- & Beitragseinstellungen',
                    billingLabel: 'Rechnungslabels',
                    invoice: 'Rechnungen & Spenden',
                    cashAccountOrderStatus: 'Zahlungsauslösungen',
                    // accounting
                    accounting: 'Buchhaltung',
                    bookEntry: 'Buchungsjournal & Auswertungen',
                    // passes
                    walletPass: 'Digitale Ausweise / Karten',
                    walletPassTemplate: 'Kartenvorlagen',
                },
                scopePermissions: {
                    read: 'Lesen',
                    insert: 'Einfügen',
                    edit: 'Bearbeiten',
                    remove: 'Löschen',
                    restricted: 'Eingeschränkt',
                },
            },
            restrictions: {
                title: 'Einschränkungen',
                scopes: {
                    mandate: 'Mandanten',
                },
                assignedOnly: {
                    title: 'Nur zugewiesene Einträge',
                    description:
                        'Beschränkt diese Bereiche auf die Einträge, die der Person zugewiesen sind, zum Beispiel zur Prüfung oder als Zuständige. In Dateien sind das die Ordner, die mit der Person geteilt sind. Andere Einträge bleiben verborgen.',
                },
            },
            subscriptionStatus: {
                trial: 'Testphase',
                active: 'Aktiv',
                inactive: 'Inaktiv',
                willCancel: 'Wird beendet',
                canceled: 'Beendet',
                trialEnded: 'Testphase beendet',
            },
            newCRMMode: {
                notAvailable: 'Nicht verfügbar',
                available: 'Verfügbar',
                replace: 'Ersetzen',
            },
            migrationStatus: {
                available: 'Verfügbar',
                inProgress: 'In Bearbeitung',
                finished: 'Abgeschlossen',
            },
            classification: {
                type: {
                    club: 'Verein',
                    association: 'Verband',
                    foundation: 'Stiftung',
                    charity: 'Wohlfahrtsorganisation',
                    church: 'Kirche',
                    political: 'Partei',
                    cooperative: 'Genossenschaft',
                    gGmbH: 'gGmbH',
                    other: 'Sonstige',
                },
                area: {
                    culture: 'Kultur, Bildung, Wissenschaft',
                    political: 'Politik',
                    business: 'Wirtschaft',
                    environment: 'Umwelt',
                    human: 'Menschenrechte und humanitäre Zwecke',
                    relax: 'Freizeit und Erholung',
                    sports: 'Sport',
                    other: 'Sonstige',
                },
                sports: {
                    airSports: 'Luftsport',
                    rockClimbing: 'Bergsteigen',
                    americanFootball: 'American Football',
                    cheerleading: 'Cheerleading',
                    badminton: 'Badminton',
                    baseball: 'Baseball',
                    softball: 'Softball',
                    basketball: 'Basketball',
                    disabilitySports: 'Sport von Menschen mit Behinderung',
                    billiards: 'Billard',
                    bobsleigh: 'Bobsport',
                    tobogganing: 'Rodeln',
                    skeleton: 'Skeleton',
                    boccia: 'Boccia',
                    boule: 'Boule',
                    petanque: 'Pétanque',
                    boxing: 'Boxen',
                    cricket: 'Cricket',
                    curling: 'Curling',
                    darts: 'Darts',
                    iceHockey: 'Eishockey',
                    figureSkating: 'Eiskunstlauf',
                    iceDancing: 'Eistanzen',
                    speedSkating: 'Eisschnelllauf',
                    shortTrack: 'Shorttrack',
                    stickShooting: 'Stockschießen',
                    fencing: 'Fechten',
                    floorball: 'Floorball',
                    football: 'Fußball',
                    deafSports: 'Gehörlosensport',
                    weightLifting: 'Gewichtheben',
                    golf: 'Golf',
                    handball: 'Handball',
                    hockey: 'Hockey',
                    judo: 'Judo',
                    jujutsu: 'Ju-Jutsu',
                    canoeing: 'Kanusport',
                    karate: 'Karate',
                    bowling: 'Bowling',
                    kickboxing: 'Kickboxen',
                    powerLifting: 'Kraftdreikampf',
                    rescueSports: 'Rettungssport',
                    athletics: 'Leichtathletik',
                    miniGolf: 'Minigolf',
                    motorsport: 'Motorsport',
                    motorboating: 'Motorbootsport',
                    cycling: 'Radsport',
                    lawnPowerSports: 'Rasenkraftsport',
                    tugOfWar: 'Tauziehen',
                    equestrianSports: 'Pferdesport',
                    wrestling: 'Ringen',
                    roller: 'Rollsport',
                    inlineSkating: 'Inlineskaten',
                    rowing: 'Rudern',
                    rugby: 'Rugby',
                    chess: 'Schach',
                    shooting: 'Sportschießen',
                    swimming: 'Schwimmen',
                    sailing: 'Segelsport',
                    skibob: 'Skibob',
                    ski: 'Skisport',
                    acrobatics: 'Sportakrobatik',
                    scuba: 'Sporttauchen',
                    squash: 'Squash',
                    taekwondo: 'Taekwondo',
                    dancing: 'Tanzsport',
                    tennis: 'Tennis',
                    tableTennis: 'Tischtennis',
                    triathlon: 'Triathlon',
                    gymnastics: 'Turnen',
                    snowboard: 'Snowboard',
                    pentathlon: 'Moderner Fünfkampf',
                    volleyball: 'Volleyball',
                    waterSki: 'Wasserski',
                    wakeboard: 'Wakeboard',
                    surfing: 'Surfen',
                    martialArts: 'Kampfkunst',
                    kungFu: 'Kung Fu',
                    fistball: 'Faustball',
                    eSports: 'E-Sports',
                    aikido: 'Aikido',
                    multispeciality: 'Mehrspartenvereine',
                    archery: 'Bogenschießen',
                    standUpPaddle: 'StandUp Paddling',
                    taiChi: 'Tai-chi',
                    qiGong: 'Qi-gong',
                    sanda: 'Sanda',
                    selfDefense: 'Selbstverteidigung',
                    fitness: 'Fitness/Gesundheitssport',
                    childrenSport: 'Kindersport',
                    running: 'Lauf-/Ausdauersport',
                    ropeSkipping: 'Rope Skipping',
                    shuttlecock: 'Federball',
                    fingerBoarding: 'Fingerboarding',
                    flagFootball: 'Flag Football',
                    flightModel: 'Flugmodellsport',
                    frisbee: 'Frisbee',
                    climbing: '(Sport-)Klettern',
                    walking: 'Gehen',
                    geocaching: 'Geocaching',
                    paragliding: 'Gleitschirmfliegen',
                    jiuJutsu: 'Jiu-Jutsu',
                    kendo: 'Kendo',
                    kravMaga: 'Krav Maga',
                    lacrosse: 'Lacrosse',
                    mountainBiking: 'Mountainbiking',
                    muayThai: 'Muay Thai',
                    nordicWalking: 'Nordic walking',
                    orienteeringRace: 'Orientierungslauf',
                    paintBall: 'Paintball',
                    parkour: 'Parkour',
                    poker: 'Poker',
                    polo: 'Polo',
                    poleDance: 'Pole Dance',
                    prellball: 'Prellball',
                    racketlon: 'Racketlon',
                    waterSport: 'Wassersport',
                    skateboarding: 'Skateboarding',
                    stockschiessen: 'Stockschießen',
                    wingTsun: 'Wing Tsun',
                    wushu: 'Wushu',
                    circus: 'Zirkus',
                    popularSport: 'Breitensport',
                    other: 'Sonstige Sportart',
                    skiing: 'Skiing',
                    snowboarding: 'Snowboarding',
                    crossCountrySkiing: 'Langlauf',
                },
            },
        },
        booking: {
            getBookingRateStartPriceString: {
                free: 'Kostenlos',
                startingAt: 'Ab {{price}}',
            },
        },
        crm: {
            contactRule: {
                contentRequired: 'Gib einen Nachrichteninhalt ein, wenn das Design einen Inhaltsplatzhalter enthält.',
                letterTemplateRequired: 'Eine Briefvorlage ist erforderlich',
                missingReceiverPlaceholder: 'Die Briefvorlage muss den Platzhalter "Empfänger" enthalten.',
                triggerTypeCanNotChange: 'Der Auslösertyp kann nicht geändert werden',
                invalidTriggerTypeForContactType: 'Der ausgewählte Auslöser ist für diesen Kontakttyp nicht gültig',
                updatedTriggerPropertiesRequired: 'Wähle mindestens eine geänderte Eigenschaft aus',
                updatedTriggerPropertiesDuplicate: 'Geänderte Eigenschaften müssen eindeutig sein',
                invalidUpdatedTriggerProperty: 'Die ausgewählte geänderte Eigenschaft ist ungültig',
                cannotSetShiftTimeToTimeType:
                    'Der Verschiebungszeitpunkt kann nicht auf wiederkehrenden Zeitpunkt gesetzt werden',
                cannotSetShiftTimeBeforeForImmediateTrigger:
                    'Der Verschiebungszeitpunkt kann nicht auf vor dem ausgewählten Auslösertyp gesetzt werden',
            },
            contract: {
                createContractPeriodDatesProperties: {
                    groupName: 'Zeiträume',
                    periodStart: 'Abrechnungsbeginn',
                    periodEnd: 'Abrechnungsende',
                    intervalStart: 'Intervallbeginn',
                    intervalEnd: 'Intervallende',
                },
                contractTextTags: {
                    intervalStart: 'Intervallbeginn',
                    intervalEnd: 'Intervallende',
                    periodStart: 'Abrechnungsbeginn',
                    periodEnd: 'Abrechnungsende',
                    collection: 'Einzug',
                    receiptFinal: 'Beleg',
                    collectAt: 'Einzugsdatum',
                    dueAt: 'Fälligkeitsdatum',
                    amount: 'Betrag',
                    totalAmountToPay: 'Zu zahlender Gesamtbetrag',
                    onlinePaymentLink: 'Online-Zahlungslink',
                    contributions: 'Verträge',
                    debtorReceiptSendMethod: 'Versandmethode',
                    debtorPaymentMethodType: 'Zahlungsmethode',
                    debtorSepaAccountHolderName: 'SEPA-Kontoinhaber',
                    dateTags: {
                        date: 'Formatiertes Datum',
                        day: 'Tag des Monats',
                        month: 'Monatsnummer',
                        monthName: 'Monatsname',
                        monthNameShort: 'Monatsname kurz',
                        quarter: 'Quartalsnummer',
                        quarterName: 'Quartalsname',
                        halfYear: 'Halbjahresnummer',
                        halfYearName: 'Halbjahresname',
                        year: 'Jahresnummer',
                    },
                },
            },
            contact: {
                getContactGreetings: {
                    male: 'Sehr geehrter Herr {{lastName}}',
                    female: 'Sehr geehrte Frau {{lastName}}',
                    diverse: 'Sehr geehrte(r) {{lastName}}',
                    neutral: 'Hallo {{firstName}} {{lastName}}',
                    institution: 'Sehr geehrte Damen und Herren',
                },
            },
            pass: {
                getDefaultEmailTexts: {
                    subject: 'Dein digitaler Pass',
                    message: {
                        greetings: 'Hallo [[Name]],',
                        intro: 'Dein neuer digitaler Pass ist verfügbar.',
                        downloadLink:
                            'Um die Karte herunterzuladen, klicke auf den folgenden Link, je nachdem, welches Mobiltelefon du verwendest:',
                    },
                },
                composition: {
                    groupName: 'Pässe',
                    barcode: 'Barcode-Daten',
                    validFrom: 'Gültig ab',
                    validTo: 'Gültig bis',
                },
            },
            mailing: {
                compositionProperties: {
                    mailing: {
                        groupName: 'Rundschreiben',
                        unsubscribeLink: {
                            name: 'Abmeldelink',
                        },
                    },
                },
            },
            communication: {
                contactMessageDesign: {
                    receiverRequired: 'Füge die Empfängeradresse in jeder Sprache der Briefvorlage ein.',
                    sharedPropertiesRequired: 'Das geteilte Segment benötigt die Eigenschaften Name und E-Mail.',
                    sharedAddressRequired:
                        'Das geteilte Segment benötigt die Eigenschaften Straße, Ort und Postleitzahl.',
                    sharedAlternativesRequired:
                        'Das geteilte Segment benötigt Namen und E-Mail-Adressen für alternative Empfänger.',
                },
                templateDesign: {
                    unsubscribe: 'Abbestellen',
                    emailTitle: 'Nachrichtenvorlage',
                    emailContent:
                        'Hallo.<br><br>Dies ist der Beispielinhalt der Vorlage. Du kannst ihn nach Belieben anpassen und an verschiedenen Stellen auf Campai wiederverwenden.<br><br>Viel Spaß!',
                    letterTitle: 'Titel der Vorlage',
                    greeting: 'Hallo',
                    letterContent: 'Dies ist ein Beispiel-Inhalt.',
                },
                createLetterCompositionProperties: {
                    letterAddress: {
                        groupName: 'Vollständige Briefadresse',
                        sender: {
                            name: 'Absenderadresse',
                        },
                        receiver: {
                            name: 'Empfängeradresse',
                        },
                    },
                },
            },
        },
        finances: {
            accounting: {
                formatAccountBalance: {
                    balanceType: {
                        debit: 'S',
                        credit: 'H',
                    },
                },
            },
            receipts: {
                receiptDocument: {
                    shared: {
                        properties: {
                            sender: {
                                name: 'Versender',
                                description: 'Komplette Versender-Details mit Namen und Adresse',
                            },
                            receiver: {
                                name: 'Empfänger',
                                description: 'Komplette Empfänger-Details mit Namen und Adresse',
                            },
                            customerType: {
                                name: 'Kunden-Art',
                                description: 'Die Art des Debitors z.B. "Kundennummer"',
                            },
                            customerNumber: {
                                name: 'Kundennummer',
                                description: 'Die Kunden / Mitglieds- oder Debitorennummer, kann leer sein',
                            },
                            paymentTerms: {
                                name: 'Zahlungsbedingungen',
                                description: 'Die Zahlungsbedingungen des Belegs, kann leer sein',
                            },
                            epcQRData: {
                                name: 'Girocode',
                                description: 'Die Daten für den Girocode, kann leer sein',
                            },
                        },
                    },
                    receipt: {
                        properties: {
                            title: {
                                name: 'Titel',
                                description: 'Der Titel des Belegs z.B. "Rechnung"',
                            },
                            intro: {
                                name: 'Einleitungstext',
                                description: 'Der Einleitungstext des Belegs, kann leer sein',
                            },
                            deliveryTerms: {
                                name: 'Lieferbedingungen',
                                description: 'Die Lieferbedingungen des Belegs, kann leer sein',
                            },
                            note: {
                                name: 'Notizen',
                                description: 'Die abschließenden Notizen des Belegs, kann leer sein',
                            },
                            receiptDate: {
                                name: 'Belegdatum',
                                description: 'Datum, wann der Beleg erstellt wurde (z.B. Rechnungsdatum)',
                            },
                            receiptNumber: {
                                name: 'Belegnummer',
                                description: 'Die Belegnummer (z.B. Rechnungsnummer)',
                            },
                            dueDate: {
                                name: 'Fälligkeitsdatum',
                                description: 'Das Fälligkeitsdatum des Belegs, kann leer sein',
                            },
                            deliveryDateType: {
                                name: 'Lieferart',
                                description: 'Die Lieferart z.B. "Lieferdatum", kann leer sein',
                            },
                            deliveryDate: {
                                name: 'Lieferdatum',
                                description: 'Das Lieferdatum, kann leer sein',
                            },
                            positions: {
                                name: 'Positionen',
                                description: 'Ein oder mehrere Positionen des Belegs',
                                properties: {
                                    position: {
                                        name: 'Position',
                                        description: 'Die Nummer der Position',
                                    },
                                    description: {
                                        name: 'Beschreibung',
                                        description: 'Beschreibung / Name der Position',
                                    },
                                    details: {
                                        name: 'Details',
                                        description: 'Zusätzliche, optionale Beschreibung der Position, kann leer sein',
                                    },
                                    quantity: {
                                        name: 'Menge',
                                        description: 'Die Menge der Position, kann leer sein',
                                    },
                                    unit: {
                                        name: 'Einheit',
                                        description: 'Die Einheit der Position, kann leer sein',
                                    },
                                    unitAmount: {
                                        name: 'Einheitspreis',
                                        description: 'Preis pro Einheit der Position, kann leer sein',
                                    },
                                    total: {
                                        name: 'Summe',
                                        description: 'Die Gesamtsumme der Position',
                                    },
                                    tax: {
                                        name: 'Steuer',
                                        description: 'Die Steuer der Position, kann leer sein',
                                    },
                                    discount: {
                                        name: 'Rabatt',
                                        description: 'Der Rabatt der Position, kann leer sein',
                                    },
                                },
                            },
                            totals: {
                                name: 'Summen',
                                description: 'Liste von Gesamtpreis, Rabatte, eventuelle Steuern und genutzte Guthaben',
                                properties: {
                                    type: {
                                        name: 'Art',
                                        description: 'Die Art der Zeile, eins von subTotal | total | tax | discount',
                                    },
                                    label: {
                                        name: 'Bezeichnung',
                                        description: 'Die Bezeichnung der Zeile (z.B. Steuer oder Gesamtpreis)',
                                    },
                                    value: {
                                        name: 'Wert',
                                        description: 'Der Wert der Zeile',
                                    },
                                },
                                labels: {
                                    includedTaxes: 'Enthaltene Steuer',
                                    discount: 'Rabatt',
                                    depositUsed: 'Bereits bezahlt',
                                    subTotalNet: 'Zwischensumme netto',
                                    subTotalGross: 'Zwischensumme',
                                    totalNetAmount: 'Gesamt netto',
                                    totalGrossAmount: 'Gesamtbetrag',
                                },
                            },
                        },
                    },
                    reminder: {
                        payment: 'Teilzahlung',
                        levels: {
                            level1: {
                                fee: 'Erinnerung Mahngebühren',
                            },
                            level2: {
                                fee: 'Erste Mahnung Mahngebühren',
                            },
                            level3: {
                                fee: 'Zweite Mahnung Mahngebühren',
                            },
                            level4: {
                                fee: 'Dritte Mahnung Mahngebühren',
                            },
                        },
                        properties: {
                            title: {
                                name: 'Titel',
                                description: 'Der Titel der Mahnung z.B. "Zahlungserinnerung"',
                            },
                            intro: {
                                name: 'Einleitungstext',
                                description: 'Der Einleitungstext des Belegs, kann leer sein',
                            },
                            note: {
                                name: 'Beleg Fußzeile',
                                description: 'Die abschließenden Notizen des Belegs, kann leer sein',
                            },
                            unpaidAmount: {
                                name: 'Offener Gesamtbetrag',
                                description:
                                    'Der offene Gesamtbetrag, inklusive aktueller und aller vorherigen Mahngebühren',
                            },
                            fee: {
                                name: 'Gebühren',
                                description: 'Die Gebühren für die aktuelle Mahnung',
                            },
                            date: {
                                name: 'Datum',
                                description: 'Datum der Mahnung',
                            },
                            receiptTitle: {
                                name: 'Belegtitel',
                                description: 'Titel des Originalbelegs, z.B. "Rechnung"',
                            },
                            receiptDate: {
                                name: 'Belegdatum',
                                description: 'Das Datum des Originalbelegs, z.B. das "Rechnungsdatum"',
                            },
                            receiptNumber: {
                                name: 'Belegnummer',
                                description: 'Die Nummer des Originalbelegs, z.B. die "Rechnungsnummer"',
                            },
                            receiptDueDate: {
                                name: 'Beleg-Fälligkeitsdatum',
                                description: 'Das Fälligkeitsdatum des Originalbelegs',
                            },
                            receiptGrossAmount: {
                                name: 'Beleg Originalbetrag',
                                description: 'Der Betrag des Originalbelegs',
                            },
                            receiptFees: {
                                name: 'Gebühren',
                                description: 'Die gesamten Gebühren des ursprünglichen Belegs',
                            },
                            assessments: {
                                name: 'Mahnstufen',
                                description:
                                    'Eine Übersicht der Zahlungserinnerungsgebühren – erste, zweite und letzte Mahnstufe oder Zahlungen',
                            },
                        },
                    },
                    returnedPayment: {
                        properties: {
                            title: {
                                name: 'Titel',
                                description: 'Der Titel der Benachrichtigung',
                            },
                            intro: {
                                name: 'Einleitungstext',
                                description: 'Der Einleitungstext der Benachrichtigung, kann leer sein',
                            },
                            note: {
                                name: 'Beleg Fußzeile',
                                description: 'Die abschließenden Notizen der Benachrichtigung, kann leer sein',
                            },
                            unpaidAmount: {
                                name: 'Offener Betrag',
                                description: 'Der offene Betrag, inklusive eventueller Gebühren',
                            },
                            returnFee: {
                                name: 'Rückläufergebühren',
                                description: 'Die von der Bank berechneten Gebühren für den Rückläufer',
                            },
                            processingFee: {
                                name: 'Bearbeitungsgebühren',
                                description: 'Optionale, zusätzliche Bearbeitungsgebühren',
                            },
                            date: {
                                name: 'Datum',
                                description: 'Das Datum der Benachrichtigung',
                            },
                            receiptTitle: {
                                name: 'Belegtitel',
                                description: 'Titel des Originalbelegs, z.B. "Rechnung"',
                            },
                            receiptDate: {
                                name: 'Belegdatum',
                                description: 'Das Datum des Originalbelegs, z.B. das "Rechnungsdatum"',
                            },
                            receiptNumber: {
                                name: 'Belegnummer',
                                description: 'Die Nummer des Originalbelegs, z.B. die "Rechnungsnummer"',
                            },
                            receiptDueDate: {
                                name: 'Beleg-Fälligkeitsdatum',
                                description: 'Das Fälligkeitsdatum des Originalbelegs',
                            },
                            receiptGrossAmount: {
                                name: 'Beleg Originalbetrag',
                                description: 'Der Betrag des Originalbelegs',
                            },
                        },
                    },
                },
                receiptTextTags: {
                    accountName: {
                        name: 'Name',
                        description: 'Der volle Name des Empfängers (Vor- und Nachname oder Name der Organisation)',
                    },
                    accountFirstName: {
                        name: 'Vorname',
                        description: 'Vorname des Empfängers',
                    },
                    accountLastName: {
                        name: 'Nachname',
                        description: 'Nachname des Empfängers',
                    },
                    customerNumber: {
                        name: 'Kundennummer',
                        description: 'Kundennummer des Empfängers',
                    },
                    totalDueAmount: {
                        name: 'Fälliger Betrag',
                        description:
                            'Der fällige Betrag inklusive optionaler Gebühren und abzüglich geleisteter Zahlungen',
                    },
                    totalFeesAmount: {
                        name: 'Gebühren',
                        description: 'Optionale Gebühren',
                    },
                    totalGrossAmount: {
                        name: 'Gesamtbetrag',
                        description: 'Der reine Gesamtbetrag des Belegs ohne Gebühren',
                    },
                    totalPaidAmount: {
                        name: 'Bezahlter Betrag',
                        description: 'Optional bereits bezahlter anteiliger Betrag',
                    },
                    dueDate: {
                        name: 'Fällig zum',
                        description: 'Fälligkeit des Belegs, z.B. das "Rechnungsdatum"',
                    },
                    receiptNumber: {
                        name: 'Belegnummer',
                        description: 'Die Belegnummer, z.B. die "Rechnungsnummer"',
                    },
                    sepaCreditTranfer: {
                        iban: {
                            name: 'Bank IBAN',
                            description: 'Die IBAN deines Bankkontos wohin überwiesen werden soll',
                        },
                        bic: {
                            name: 'Bank BIC',
                            description: 'Die BIC deines Bankkontos wohin überwiesen werden soll',
                        },
                        bankName: {
                            name: 'Bank Name',
                            description: 'Der Name deiner Bank wohin überwiesen werden soll',
                        },
                        referenceId: {
                            name: 'Verwendungszweck',
                            description: 'Die Referenznummer, die als Verwendungszweck verwendet werden soll',
                        },
                    },
                    sepaDirectDebit: {
                        creditorIdentifier: {
                            name: 'Gläubiger-ID',
                            description: 'Deine Gläubiger-ID zur Verwendung der Lastschriften',
                        },
                        iban: {
                            name: 'Zahler IBAN',
                            description: 'Die IBAN des Lastschriftzahlers, von dem eingezogen wird',
                        },
                        mandateId: {
                            name: 'Mandatsreferenz',
                            description: 'Die Mandatsreferenz des Lastschriftzahlers, von dem eingezogen wird',
                        },
                    },
                },
                receiptPaymentTerms: {
                    paymentDetails:
                        'Zahlung per {{method}} am {{date}} erhalten – Vielen Dank! Es sind keine weiteren Maßnahmen erforderlich.',
                    paymentType: {
                        deposit: 'Anzahlung',
                    },
                },
            },
        },
        portal: {
            channel: {
                allMention: 'alle',
            },
            email: {
                subject: '{{organizationName}} hat dich eingeladen, der Mitglieder-Community beizutreten 🎉',
                invalidSubject: 'Der Betreff darf nur eine Zeile mit höchstens 998 Zeichen enthalten.',
                missingInvitationLink:
                    'Füge den Einladungslink in jeder Sprache im sichtbaren Inhalt oder als Link ein.',
                missingInvitationToken: 'Füge den Einladungscode in jeder Sprache im sichtbaren Inhalt ein.',
                properties: {
                    recipientName: {
                        name: 'Empfänger Name',
                        description: 'Der Name des Empfängers',
                    },
                    organizationName: {
                        name: 'Organisationsname',
                        description: 'Der Name der Organisation',
                    },
                    invitationLink: {
                        name: 'Einladungslink',
                        description: 'Der Link zur Einladung',
                    },
                    invitationToken: {
                        name: 'Einladungstoken',
                        description: 'Der Token zur Einladung',
                    },
                },
            },
        },
        simpleForm: {
            transformValue: {
                transformContext: 'Wert wird mit "{{transform}}" transformiert',
            },
            evaluateSimpleForm: {
                fieldNotFound: 'Kein Feld mit der ID "{{fieldId}}" gefunden',
            },
            validation: {
                invalidValue: 'Ungültiger Wert',
            },
        },
        misc: {
            default: 'Standard',
            total: 'Gesamt',
            unknown: 'Unbekannt',
            notSet: 'Nicht gesetzt',
            close: 'Schließen',
            fullYear: 'Ganzes Jahr',
            today: 'Heute',
            yesterday: 'Gestern',
            tomorrow: 'Morgen',
            untitled: 'Unbetitelt',
            and: 'und',
            or: 'oder',
            yes: 'Ja',
            no: 'Nein',
            numberSeparatorWord: 'Komma',
            distanceUnitShort: {
                meter: 'm',
                kilometer: 'km',
                mile: 'mi',
            },
            dateTimeFromTo: '{{date}} von {{from}} bis {{to}} Uhr',
            dateFromTo: '{{from}} bis {{to}}',
            dateFrom: 'Von',
            dateTo: 'Bis',
            point: 'Punkt',
            pointShort: 'Pk',
            points: 'Punkte',
            pointsShort: 'Pkt',
            freePrice: 'Kostenlos',
            unit: 'Einheit',
            units: 'Einheiten',
            copyOf: 'Kopie von {{name}}',
            item: 'Eintrag',
            items: 'Einträge',
            formatSequenceNumber: {
                duplicateCounterPlaceholder:
                    'Es wurden weitere Zählerplatzhalter an Position {{position}} gefunden, es ist jedoch nur einer erlaubt',
                invalidCounterPlaceholder: 'Ungültiger Zählerplatzhalter an Position {{position}}, erwartet Ziffer 1-9',
                missingCounterPlaceholder: 'Fehlender Platzhalter für den Zähler',
                counterTypeNotAllowed: 'Der ausgewählte Zählerplatzhalter ist nicht erlaubt',
            },
            selectionCount: '{{count}} ausgewählt',
        },
    },
}
